import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Modal,
  Animated,
  Dimensions,
  FlatList,
  Alert,
  RefreshControl,
  ActivityIndicator,
  Platform,
  Share,
  BackHandler,
  KeyboardAvoidingView,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons, MaterialCommunityIcons, FontAwesome5, MaterialIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';

let aiService = null;
try { aiService = require('../../../services/aiService'); } catch (e) {}

const { width, height } = Dimensions.get('window');

const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  deals: `${API_BASE_URL}/api/data/crm_deals`,
  activities: `${API_BASE_URL}/api/data/crm_activities`,
  clients: `${API_BASE_URL}/api/data/crm_clients`,
  contacts: `${API_BASE_URL}/api/data/crm_contacts`,
  stats: `${API_BASE_URL}/api/data/crm_stats_history`,
  users: `${API_BASE_URL}/api/data/business_users`,
  teams: `${API_BASE_URL}/api/data/team_departments`,
  forecast: `${API_BASE_URL}/api/data/crm_forecast`,
  notes: `${API_BASE_URL}/api/data/crm_notes`,
  emails: `${API_BASE_URL}/api/data/crm_emails`,
};

let CACHE_KEY = 'crm_cache_v3';
const CACHE_TTL = 15 * 60 * 1000;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
});
api.interceptors.request.use(async (config) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token') || await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    config.headers['X-API-Key'] = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
  } catch {}
  return config;
}, (e) => Promise.reject(e));
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) Alert.alert('Session Expired', 'Please login again');
    return Promise.reject(error);
  }
);

const BRAND = {
  blue: '#0e4e96', canary: '#fdb913', mint: '#19abb5',
  bg: '#f8fafc', text: '#0f172a', sub: '#475569', line: '#e2e8f0',
  white: '#ffffff', success: '#10b981', warning: '#f59e0b',
  danger: '#ef4444', info: '#3b82f6', purple: '#8b5cf6',
};

const STAGES = [
  { id: 'lead', name: 'Lead', color: '#94a3b8', icon: 'person-add-outline', order: 1 },
  { id: 'qualified', name: 'Qualified', color: BRAND.blue, icon: 'checkmark-circle-outline', order: 2 },
  { id: 'proposal', name: 'Proposal', color: BRAND.mint, icon: 'document-text-outline', order: 3 },
  { id: 'negotiation', name: 'Negotiation', color: BRAND.canary, icon: 'git-compare-outline', order: 4 },
  { id: 'won', name: 'Won', color: BRAND.success, icon: 'trophy-outline', order: 5 },
  { id: 'lost', name: 'Lost', color: BRAND.danger, icon: 'close-circle-outline', order: 6 },
];
const PRIORITIES = [
  { id: 'low', name: 'Low', color: BRAND.success },
  { id: 'medium', name: 'Medium', color: BRAND.warning },
  { id: 'high', name: 'High', color: BRAND.danger },
  { id: 'critical', name: 'Critical', color: '#7c3aed' },
];
const ACTIVITY_TYPES = [
  { id: 'call', name: 'Call', icon: 'call-outline', color: BRAND.blue },
  { id: 'email', name: 'Email', icon: 'mail-outline', color: BRAND.mint },
  { id: 'meeting', name: 'Meeting', icon: 'people-outline', color: BRAND.canary },
  { id: 'task', name: 'Task', icon: 'checkbox-outline', color: BRAND.success },
  { id: 'note', name: 'Note', icon: 'document-text-outline', color: BRAND.sub },
  { id: 'demo', name: 'Demo', icon: 'desktop-outline', color: BRAND.purple },
  { id: 'follow_up', name: 'Follow Up', icon: 'refresh-outline', color: BRAND.warning },
];
const DEAL_SOURCES = [
  { id: 'direct', name: 'Direct' }, { id: 'referral', name: 'Referral' },
  { id: 'website', name: 'Website' }, { id: 'linkedin', name: 'LinkedIn' },
  { id: 'cold_outreach', name: 'Cold Outreach' }, { id: 'event', name: 'Event' },
  { id: 'partner', name: 'Partner' }, { id: 'inbound', name: 'Inbound' },
];
const CONTACT_ROLES = [
  'CEO', 'CTO', 'CFO', 'COO', 'VP Sales', 'VP Engineering', 'HR Director',
  'Procurement Manager', 'IT Manager', 'Operations Manager', 'Other',
];
const EMAIL_TEMPLATES = [
  {
    id: 'intro', name: 'Introduction', icon: 'hand-left-outline',
    subject: 'Introduction from {{rep_name}} at JobEng',
    body: `Hi {{contact_name}},\n\nI hope this finds you well. My name is {{rep_name}} and I work with JobEng, helping companies like {{company}} streamline their hiring and talent pipeline.\n\nI'd love to schedule a quick 15-minute call to understand your current recruitment challenges and share how we've helped similar businesses.\n\nWould you be available for a brief call this week?\n\nBest regards,\n{{rep_name}}`,
  },
  {
    id: 'follow_up', name: 'Follow Up', icon: 'refresh-outline',
    subject: 'Following up on our conversation – {{company}}',
    body: `Hi {{contact_name}},\n\nI wanted to follow up on our recent conversation about {{deal_title}}.\n\nAs discussed, JobEng can help {{company}} with:\n• Reducing time-to-hire by up to 40%\n• Access to pre-vetted candidates\n• AI-powered matching technology\n\nI've attached our proposal for your review. Do you have any questions I can address?\n\nLooking forward to hearing from you.\n\nBest,\n{{rep_name}}`,
  },
  {
    id: 'proposal', name: 'Proposal', icon: 'document-outline',
    subject: 'JobEng Proposal for {{company}}',
    body: `Dear {{contact_name}},\n\nThank you for the opportunity to present our proposal for {{company}}.\n\nBased on our discovery conversations, here is our tailored solution:\n\n📋 SCOPE OF WORK\n- Dedicated account manager\n- Unlimited job postings for 12 months\n- AI screening & shortlisting\n- Interview scheduling automation\n\n💰 INVESTMENT\n£{{deal_value}} per annum (with 10% early-commitment discount available)\n\nNext Steps: I'd love to walk you through the proposal on a 30-min call.\n\nKind regards,\n{{rep_name}}`,
  },
  {
    id: 'closing', name: 'Closing', icon: 'checkmark-done-outline',
    subject: 'Ready to move forward with {{company}}?',
    body: `Hi {{contact_name}},\n\nI'm reaching out as we discussed moving forward with JobEng for {{company}}.\n\nThe offer we discussed includes everything we've outlined, and the £{{deal_value}} proposal is valid until {{due_date}}.\n\nTo get started, I just need:\n1. Signed agreement (attached)\n2. Billing details\n3. Onboarding call scheduled\n\nShall we lock in your spot this week?\n\nBest,\n{{rep_name}}`,
  },
  {
    id: 'nurture', name: 'Nurture', icon: 'leaf-outline',
    subject: 'Thought this might be useful for {{company}}',
    body: `Hi {{contact_name}},\n\nI came across this insight and immediately thought of you.\n\nWith the current talent market, companies are finding it harder to fill specialist roles quickly. We've just published a report showing the top 5 strategies that reduced time-to-hire by 35% in 2024.\n\nHappy to share the full report — just reply and I'll send it over.\n\nNo sales pitch, genuinely think it's relevant to {{company}}.\n\nBest,\n{{rep_name}}`,
  },
];

const MOCK = {
  deals: [
    { id: 'deal_1', company: 'TechCorp Solutions', contact_name: 'Sarah Mitchell', contact_email: 'sarah@techcorp.io', contact_phone: '+44 7700 900001', stage: 'negotiation', value: 45000, probability: 75, priority: 'high', source: 'referral', next_action: 'Send revised proposal by Friday', due_date: new Date(Date.now() + 7 * 86400000).toISOString(), tags: ['Enterprise', 'Tech', 'Urgent'], notes: 'Very interested in the AI screening feature. Budget approved up to £50k.', assigned_user: { id: 'u1', name: 'James Carter' }, score: 87, created_at: new Date(Date.now() - 15 * 86400000).toISOString() },
    { id: 'deal_2', company: 'Finsbury Capital', contact_name: 'Robert Chen', contact_email: 'r.chen@finsbury.com', contact_phone: '+44 7700 900002', stage: 'proposal', value: 28000, probability: 55, priority: 'medium', source: 'linkedin', next_action: 'Demo call scheduled', due_date: new Date(Date.now() + 14 * 86400000).toISOString(), tags: ['Finance', 'Mid-Market'], notes: 'Evaluating us vs 2 competitors. Decision by end of month.', assigned_user: { id: 'u2', name: 'Emma Wilson' }, score: 62, created_at: new Date(Date.now() - 8 * 86400000).toISOString() },
    { id: 'deal_3', company: 'GreenBuild Ltd', contact_name: 'Lisa Park', contact_email: 'l.park@greenbuild.co.uk', contact_phone: '+44 7700 900003', stage: 'qualified', value: 12000, probability: 40, priority: 'medium', source: 'website', next_action: 'Discovery call booked', due_date: new Date(Date.now() + 21 * 86400000).toISOString(), tags: ['SMB', 'Construction'], notes: 'Startup, growing fast. Price-sensitive.', assigned_user: { id: 'u1', name: 'James Carter' }, score: 45, created_at: new Date(Date.now() - 3 * 86400000).toISOString() },
    { id: 'deal_4', company: 'Nexus Healthcare', contact_name: 'Dr. Alan Foster', contact_email: 'a.foster@nexushealth.nhs.uk', contact_phone: '+44 7700 900004', stage: 'lead', value: 75000, probability: 20, priority: 'critical', source: 'event', next_action: 'Send intro pack', due_date: new Date(Date.now() + 30 * 86400000).toISOString(), tags: ['NHS', 'Healthcare', 'Large'], notes: 'Met at HR Summit. Looking to overhaul recruitment process for 5 hospitals.', assigned_user: { id: 'u3', name: 'Priya Sharma' }, score: 71, created_at: new Date(Date.now() - 1 * 86400000).toISOString() },
    { id: 'deal_5', company: 'Bright Futures Academy', contact_name: 'Mark Thompson', contact_email: 'm.thompson@bfa.edu', contact_phone: '+44 7700 900005', stage: 'won', value: 18000, probability: 100, priority: 'low', source: 'referral', next_action: 'Onboarding session', due_date: new Date(Date.now() - 2 * 86400000).toISOString(), tags: ['Education', 'Won'], notes: 'Signed off! Start onboarding next week.', assigned_user: { id: 'u2', name: 'Emma Wilson' }, score: 100, created_at: new Date(Date.now() - 30 * 86400000).toISOString() },
    { id: 'deal_6', company: 'Pinnacle Logistics', contact_name: 'Gary Walsh', contact_email: 'g.walsh@pinnacle-log.com', contact_phone: '+44 7700 900006', stage: 'lost', value: 22000, probability: 0, priority: 'low', source: 'cold_outreach', next_action: null, due_date: new Date(Date.now() - 10 * 86400000).toISOString(), tags: ['Logistics', 'Lost'], notes: 'Went with competitor on price. Revisit in Q3.', assigned_user: { id: 'u1', name: 'James Carter' }, score: 0, created_at: new Date(Date.now() - 45 * 86400000).toISOString() },
    { id: 'deal_7', company: 'Vertex Analytics', contact_name: 'Sophie Lee', contact_email: 'sophie@vertex-a.io', contact_phone: '+44 7700 900007', stage: 'proposal', value: 34000, probability: 60, priority: 'high', source: 'inbound', next_action: 'Follow up on proposal', due_date: new Date(Date.now() + 5 * 86400000).toISOString(), tags: ['SaaS', 'Analytics', 'Hot Lead'], notes: 'Inbound from our blog post. Very engaged.', assigned_user: { id: 'u3', name: 'Priya Sharma' }, score: 78, created_at: new Date(Date.now() - 6 * 86400000).toISOString() },
  ],
  contacts: [
    { id: 'c1', name: 'Sarah Mitchell', company: 'TechCorp Solutions', email: 'sarah@techcorp.io', phone: '+44 7700 900001', role: 'VP Engineering', linkedin: 'linkedin.com/in/sarahmitchell', location: 'London, UK', deals: ['deal_1'], tags: ['Key Contact', 'Decision Maker'], notes: 'Strong technical background. Key decision-maker.', last_contact: new Date(Date.now() - 2 * 86400000).toISOString(), score: 87 },
    { id: 'c2', name: 'Robert Chen', company: 'Finsbury Capital', email: 'r.chen@finsbury.com', phone: '+44 7700 900002', role: 'CFO', linkedin: 'linkedin.com/in/robertchen', location: 'City of London', deals: ['deal_2'], tags: ['Finance'], notes: 'Very analytical. Needs ROI data.', last_contact: new Date(Date.now() - 5 * 86400000).toISOString(), score: 62 },
    { id: 'c3', name: 'Lisa Park', company: 'GreenBuild Ltd', email: 'l.park@greenbuild.co.uk', phone: '+44 7700 900003', role: 'CEO', linkedin: '', location: 'Manchester', deals: ['deal_3'], tags: ['SMB', 'Startup'], notes: 'Founder-led company. Quick decisions.', last_contact: new Date(Date.now() - 1 * 86400000).toISOString(), score: 45 },
    { id: 'c4', name: 'Dr. Alan Foster', company: 'Nexus Healthcare', email: 'a.foster@nexushealth.nhs.uk', phone: '+44 7700 900004', role: 'HR Director', linkedin: 'linkedin.com/in/alanfoster', location: 'Birmingham', deals: ['deal_4'], tags: ['NHS', 'Healthcare'], notes: 'Long procurement cycles. Very process-driven.', last_contact: new Date(Date.now() - 1 * 86400000).toISOString(), score: 71 },
    { id: 'c5', name: 'Sophie Lee', company: 'Vertex Analytics', email: 'sophie@vertex-a.io', phone: '+44 7700 900007', role: 'CTO', linkedin: 'linkedin.com/in/sophielee', location: 'Edinburgh', deals: ['deal_7'], tags: ['Tech', 'Inbound'], notes: 'Technical buyer. Loves the AI features.', last_contact: new Date(Date.now() - 3 * 86400000).toISOString(), score: 78 },
  ],
  activities: [
    { id: 'act_1', type: 'call', title: 'Discovery call with TechCorp', description: 'Discuss requirements and budget', deal_id: 'deal_1', deal: { company: 'TechCorp Solutions' }, scheduled_at: new Date().toISOString(), duration: 45, completed: false, notes: '' },
    { id: 'act_2', type: 'email', title: 'Send Finsbury proposal', description: 'Revised proposal with new pricing', deal_id: 'deal_2', deal: { company: 'Finsbury Capital' }, scheduled_at: new Date().toISOString(), duration: 15, completed: true, notes: 'Sent at 9am' },
    { id: 'act_3', type: 'meeting', title: 'Nexus Healthcare intro meeting', description: 'Present company overview', deal_id: 'deal_4', deal: { company: 'Nexus Healthcare' }, scheduled_at: new Date(Date.now() + 2 * 3600000).toISOString(), duration: 60, completed: false, notes: '' },
    { id: 'act_4', type: 'follow_up', title: 'Vertex Analytics follow up', description: 'Check on proposal status', deal_id: 'deal_7', deal: { company: 'Vertex Analytics' }, scheduled_at: new Date(Date.now() + 4 * 3600000).toISOString(), duration: 20, completed: false, notes: '' },
    { id: 'act_5', type: 'demo', title: 'Product demo - GreenBuild', description: 'Showcase AI screening module', deal_id: 'deal_3', deal: { company: 'GreenBuild Ltd' }, scheduled_at: new Date(Date.now() + 6 * 3600000).toISOString(), duration: 30, completed: false, notes: '' },
  ],
  stats: { totalValue: 214000, activeDeals: 5, winRate: 58, conversionRate: 32, thisMonthGrowth: 23, averageDealSize: 30571, wonThisMonth: 3, lostThisMonth: 1, avgSalesCycle: 21 },
  users: [
    { id: 'u1', name: 'James Carter', role: 'sales', avatar: 'JC', deals: 3, revenue: 79000, winRate: 61 },
    { id: 'u2', name: 'Emma Wilson', role: 'sales', avatar: 'EW', deals: 2, revenue: 46000, winRate: 71 },
    { id: 'u3', name: 'Priya Sharma', role: 'sales', avatar: 'PS', deals: 2, revenue: 109000, winRate: 55 },
  ],
  notes: {
    deal_1: [
      { id: 'n1', text: 'Initial call went well — Sarah is the main decision maker. Budget approved.', created_by: 'James Carter', created_at: new Date(Date.now() - 10 * 86400000).toISOString() },
      { id: 'n2', text: 'Demo completed. They loved the AI screening feature. Requesting formal proposal.', created_by: 'James Carter', created_at: new Date(Date.now() - 5 * 86400000).toISOString() },
    ],
    deal_2: [
      { id: 'n3', text: 'First contact via LinkedIn. Robert is CFO and primary evaluator.', created_by: 'Emma Wilson', created_at: new Date(Date.now() - 8 * 86400000).toISOString() },
    ],
  },
  forecast: [
    { month: 'Jan', revenue: 42000, deals: 4, target: 45000 },
    { month: 'Feb', revenue: 38000, deals: 3, target: 45000 },
    { month: 'Mar', revenue: 56000, deals: 5, target: 50000 },
    { month: 'Apr', revenue: 71000, deals: 6, target: 55000 },
    { month: 'May', revenue: 65000, deals: 5, target: 60000 },
    { month: 'Jun', revenue: 89000, deals: 7, target: 65000 },
  ],
};

// ─── LEAD SCORING ENGINE ───────────────────────────────────────────────────
function calcLeadScore(deal) {
  let score = 0;
  const stage = STAGES.find(s => s.id === deal.stage);
  if (stage) score += stage.order * 10;
  if (deal.probability >= 70) score += 20;
  else if (deal.probability >= 40) score += 10;
  if (deal.value >= 50000) score += 15;
  else if (deal.value >= 20000) score += 8;
  const daysOld = (Date.now() - new Date(deal.created_at).getTime()) / 86400000;
  if (daysOld < 7) score += 10;
  if (deal.priority === 'critical') score += 15;
  else if (deal.priority === 'high') score += 10;
  if (deal.tags?.includes('Hot Lead') || deal.tags?.includes('Urgent')) score += 10;
  return Math.min(100, score);
}

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────
export default function CRMScreen({ navigation, route }) {
  // Core data state
  const [deals, setDeals] = useState([]);
  const [activities, setActivities] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [notes, setNotes] = useState({});
  const [forecastData, setForecastData] = useState([]);
  const [stats, setStats] = useState({ totalDeals: 0, wonThisMonth: 0, lostThisMonth: 0, totalValue: 0 });
  const [kpis, setKpis] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const { ask: askAI, agents: aiAgents, loading: aiLoading, history: aiHistory } = useAI('CRMScreen');
  const [showAIChat, setShowAIChat] = useState(false);

  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeStage, setActiveStage] = useState('all');
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('updated_at');
  const [filterPriority, setFilterPriority] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, hasMore: false });

  // Selected items
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  // Modal visibility
  const [showAddDeal, setShowAddDeal] = useState(false);
  const [showDealDetail, setShowDealDetail] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showContactDetail, setShowContactDetail] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showForecast, setShowForecast] = useState(false);
  const [showAIAdvisor, setShowAIAdvisor] = useState(false);
  const [showEmailTemplates, setShowEmailTemplates] = useState(false);
  const [showTeam, setShowTeam] = useState(false);
  const [showPipeline, setShowPipeline] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  // Forms
  const [dealForm, setDealForm] = useState({
    company: '', contact_name: '', contact_email: '', contact_phone: '',
    stage: 'lead', value: '', probability: 30, priority: 'medium',
    next_action: '', due_date: new Date(), tags: [], notes: '',
    source: 'direct', assigned_to: null,
  });
  const [activityForm, setActivityForm] = useState({
    type: 'call', title: '', description: '', deal_id: null, client_id: null,
    scheduled_at: new Date(), duration: 30, notes: '', completed: false,
  });
  const [contactForm, setContactForm] = useState({
    name: '', company: '', email: '', phone: '', role: '', linkedin: '', notes: '',
  });
  const [newNote, setNewNote] = useState('');
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');

  // Animations
  const pulse = useRef(new Animated.Value(0)).current;
  const scrollY = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const tabAnim = useRef(new Animated.Value(0)).current;

  // ── Lifecycle ──
  useEffect(() => {
    loadData();
    Animated.loop(Animated.sequence([
      Animated.timing(pulse, { toValue: 1, duration: 1200, useNativeDriver: true }),
      Animated.timing(pulse, { toValue: 0, duration: 1200, useNativeDriver: true }),
    ])).start();
    Animated.timing(fadeAnim, { toValue: 1, duration: 500, useNativeDriver: true }).start();
  }, []);

  useEffect(() => { updateKPIs(); }, [deals]);

  useEffect(() => {
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      if (showDealDetail || showContacts || showAnalytics || showAIAdvisor || showPipeline ||
          showForecast || showEmailTemplates || showTeam || showFilters || showNotes || showContactDetail) {
        setShowDealDetail(false); setShowContacts(false); setShowAnalytics(false);
        setShowAIAdvisor(false); setShowPipeline(false); setShowForecast(false);
        setShowEmailTemplates(false); setShowTeam(false); setShowFilters(false);
        setShowNotes(false); setShowContactDetail(false);
        return true;
      }
      return false;
    });
    return () => sub.remove();
  }, [showDealDetail, showContacts, showAnalytics, showAIAdvisor, showPipeline,
      showForecast, showEmailTemplates, showTeam, showFilters, showNotes, showContactDetail]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { if (query) fetchDeals(); }, 500);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => { fetchDeals(); }, [activeStage, filterPriority, sortBy]);

  // ── Data Loading ──
  const loadData = async () => {
    setLoading(true);
    const cid = await AsyncStorage.getItem('company_id');
    CACHE_KEY = 'crm_cache_v3_' + (cid || '');
    try {
      const cached = await AsyncStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, ts } = JSON.parse(cached);
        setDeals(data.deals || []);
        setContacts(data.contacts || []);
        setActivities(data.activities || []);
        setUsers(data.users || []);
        setStats(data.stats || { totalDeals: 0, wonThisMonth: 0, lostThisMonth: 0, totalValue: 0 });
        setNotes(data.notes || {});
        setForecastData(data.forecast || []);
        if (Date.now() - ts < CACHE_TTL) { setLoading(false); setError(null); return; }
      }
    } catch {}
    await fetchAll();
  };

  const fetchAll = async () => {
    try {
      await Promise.all([fetchDeals(), fetchActivities(), fetchContacts(), fetchStats(), fetchUsers(), fetchForecast()]);
      setError(null);
    } catch (e) {
      console.warn('API unavailable, using mock data');
      setDeals([]);
      setContacts([]);
      setActivities([]);
      setUsers([]);
      setStats({ totalDeals: 0, wonThisMonth: 0, lostThisMonth: 0, totalValue: 0 });
      setNotes({});
      setForecastData([]);
      setError(null);
    } finally {
      setLoading(false);
      saveCache();
    }
  };

  const saveCache = async () => {
    try {
      await AsyncStorage.setItem(CACHE_KEY, JSON.stringify({
        ts: Date.now(),
        data: { deals, contacts, activities, users, stats, notes, forecast: forecastData },
      }));
    } catch {}
  };

  const fetchDeals = async (page = 1, append = false) => {
    try {
      const cid = await AsyncStorage.getItem('company_id');
      const params = {
        company_id: parseInt(cid) || 0,
        page, limit: pagination.limit,
        stage: activeStage !== 'all' ? activeStage : undefined,
        search: query || undefined,
        priority: filterPriority !== 'all' ? filterPriority : undefined,
        sort: `-${sortBy}`,
      };
      const res = await api.get(API_ENDPOINTS.deals, { params });
      const { data, meta } = res.data;
      const scored = (data || []).map(d => ({ ...d, score: calcLeadScore(d) }));
      if (append) setDeals(prev => [...prev, ...scored]);
      else setDeals(scored);
      setPagination({ page: meta.current_page, limit: meta.per_page, total: meta.total, hasMore: meta.current_page < meta.last_page });
    } catch {
if (!append) setDeals([]);
    }
  };

  const fetchActivities = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const cid = await AsyncStorage.getItem('company_id');
      const res = await api.get(API_ENDPOINTS.activities, { params: { company_id: parseInt(cid)||0, limit: 20 } });
      setActivities(res.data.data || []);
    } catch { setActivities([]); }
  };

  const fetchContacts = async () => {
    try {
      const cid = await AsyncStorage.getItem('company_id');
      const res = await api.get(API_ENDPOINTS.contacts, { params: { company_id: parseInt(cid)||0, limit: 100 } });
      setContacts(res.data.data || []);
    } catch { setContacts([]); }
  };

  const fetchStats = async () => {
    try {
      const cid = await AsyncStorage.getItem('company_id');
      const res = await api.get(API_ENDPOINTS.stats, { params: { company_id: parseInt(cid)||0 } });
      setStats(res.data.data?.[0] || { totalDeals: deals.length, wonThisMonth: 0, lostThisMonth: 0, totalValue: deals.reduce((s,d)=>s+parseFloat(d.value||0),0) });
    } catch { setStats({ totalDeals: 0, wonThisMonth: 0, lostThisMonth: 0, totalValue: 0 }); }
  };

  const fetchUsers = async () => {
    try {
      const cid = await AsyncStorage.getItem('company_id');
      const res = await api.get(API_ENDPOINTS.users, { params: { company_id: parseInt(cid)||0, limit: 50 } });
      setUsers(res.data.data || []);
    } catch { setUsers([]); }
  };

  const fetchForecast = async () => {
    try {
      const cid = await AsyncStorage.getItem('company_id');
      const res = await api.get(API_ENDPOINTS.forecast, { params: { company_id: parseInt(cid)||0 } });
      setForecastData(res.data.data || []);
    } catch { setForecastData([]); }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchAll();
    setRefreshing(false);
  };

  // ── KPI Calculation ──
  const updateKPIs = () => {
    const active = deals.filter(d => !['won', 'lost'].includes(d.stage));
    const won = deals.filter(d => d.stage === 'won');
    const totalValue = active.reduce((s, d) => s + (d.value || 0), 0);
    const winRate = deals.length > 0 ? Math.round((won.length / deals.length) * 100) : 0;
    const avgScore = active.length > 0 ? Math.round(active.reduce((s, d) => s + (d.score || 0), 0) / active.length) : 0;
    setKpis([
      { id: 'k1', label: 'Active Leads', value: deals.filter(d => d.stage === 'lead').length.toString(), icon: 'person-add-outline', trend: '+12%', color: BRAND.blue },
      { id: 'k2', label: 'Open Deals', value: active.length.toString(), icon: 'briefcase-outline', trend: '+5%', color: BRAND.mint },
      { id: 'k3', label: 'Pipeline', value: `£${(totalValue / 1000).toFixed(0)}k`, icon: 'cash', trend: '+18%', color: BRAND.success },
      { id: 'k4', label: 'Win Rate', value: `${winRate}%`, icon: 'trophy-outline', trend: '+3%', color: BRAND.canary },
      { id: 'k5', label: 'Avg Score', value: `${avgScore}`, icon: 'star-outline', trend: '+7%', color: BRAND.purple },
      { id: 'k6', label: 'Avg Deal', value: `£${(stats.averageDealSize / 1000).toFixed(0)}k`, icon: 'analytics-outline', trend: '+9%', color: BRAND.warning },
    ]);
  };

  // ── CRUD Operations ──
  const createDeal = async (dealData) => {
    try {
      const _cid = await AsyncStorage.getItem('company_id'); dealData.company_id = parseInt(_cid)||0;
      const res = await api.post(API_ENDPOINTS.deals, dealData);
      const d = { ...res.data.data, score: calcLeadScore(res.data.data) };
      setDeals(prev => [d, ...prev]);
    } catch {
      const mockDeal = { ...dealData, id: `deal_${Date.now()}`, score: calcLeadScore(dealData), created_at: new Date().toISOString(), assigned_user: { name: 'You' } };
      setDeals(prev => [mockDeal, ...prev]);
    }
    Alert.alert('✅ Deal Created', `${dealData.company} has been added to your pipeline.`);
  };

  const updateDeal = async (dealId, updates) => {
    setDeals(prev => prev.map(d => d.id === dealId ? { ...d, ...updates, score: calcLeadScore({ ...d, ...updates }) } : d));
    try { await api.put(`${API_ENDPOINTS.deals}/${dealId}`, updates); } catch {}
  };

  const deleteDeal = (dealId) => {
    Alert.alert('Delete Deal', 'This will permanently remove the deal from your pipeline.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        setDeals(prev => prev.filter(d => d.id !== dealId));
        setShowDealDetail(false);
        try { await api.delete(`${API_ENDPOINTS.deals}/${dealId}`); } catch {}
      }},
    ]);
  };

  const createActivity = async (data) => {
    try {
      const _cid2 = await AsyncStorage.getItem('company_id'); data.company_id = parseInt(_cid2)||0;
      const res = await api.post(API_ENDPOINTS.activities, data);
      setActivities(prev => [res.data.data, ...prev]);
    } catch {
      const mock = { ...data, id: `act_${Date.now()}`, completed: false, deal: deals.find(d => d.id === data.deal_id) };
      setActivities(prev => [mock, ...prev]);
    }
    Alert.alert('✅ Activity Scheduled');
  };

  const toggleActivity = async (actId, completed) => {
    setActivities(prev => prev.map(a => a.id === actId ? { ...a, completed: !completed } : a));
    try { await api.put(`${API_ENDPOINTS.activities}/${actId}`, { completed: !completed }); } catch {}
  };

  const createContact = async (data) => {
    const mock = { ...data, id: `c_${Date.now()}`, deals: [], tags: [], score: 50, last_contact: new Date().toISOString() };
    setContacts(prev => [mock, ...prev]);
    Alert.alert('✅ Contact Added');
  };

  const addNoteToDeal = (dealId, text) => {
    if (!text.trim()) return;
    const note = { id: `n_${Date.now()}`, text, created_by: 'You', created_at: new Date().toISOString() };
    setNotes(prev => ({ ...prev, [dealId]: [note, ...(prev[dealId] || [])] }));
    setNewNote('');
  };

  const handleStageChange = async (dealId, newStage) => {
    await updateDeal(dealId, { stage: newStage });
    Alert.alert('Stage Updated', `Deal moved to ${STAGES.find(s => s.id === newStage)?.name || newStage}`);
  };

  // ── AI Advisor ──
  const handleAskAI = async (prompt) => {
    if (!prompt.trim()) return;
    setAiQuery('');
    const context = {
      pipelineDeals: deals.length,
      totalValue: stats.totalValue,
      winRate: stats.winRate,
      activeDeals: deals.filter(d => !['won','lost'].includes(d.stage)).map(d => `${d.company} (${d.stage}, £${d.value})`).join(', '),
    };
    await askAI(prompt, context);
  };

  // ── Export / Share ──
  const exportPipelineReport = async () => {
    const active = deals.filter(d => !['won', 'lost'].includes(d.stage));
    const lines = [
      `JOBENG CRM PIPELINE REPORT`,
      `Generated: ${new Date().toLocaleDateString()}`,
      `─────────────────────────────────────`,
      `SUMMARY`,
      `Total Deals: ${deals.length}`,
      `Pipeline Value: £${active.reduce((s, d) => s + (d.value || 0), 0).toLocaleString()}`,
      `Win Rate: ${stats.winRate}%`,
      `Avg Deal Size: £${stats.averageDealSize?.toLocaleString()}`,
      ``,
      `ACTIVE DEALS`,
      ...active.map(d => `• ${d.company} | ${d.stage} | £${d.value?.toLocaleString()} | ${d.probability}% | Score: ${d.score}`),
      ``,
      `WON THIS MONTH: ${deals.filter(d => d.stage === 'won').length} deals`,
    ].join('\n');
    await Share.share({ message: lines, title: 'CRM Pipeline Report' });
  };

  // ── Filtered Deals (memoized) ──
  const filteredDeals = useMemo(() => {
    let d = [...deals];
    if (activeStage !== 'all') d = d.filter(x => x.stage === activeStage);
    if (filterPriority !== 'all') d = d.filter(x => x.priority === filterPriority);
    if (query) d = d.filter(x => String(x.company ?? '').toLowerCase().includes(query.toLowerCase()) || x.contact_name?.toLowerCase().includes(query.toLowerCase()));
    return d.sort((a, b) => {
      if (sortBy === 'value') return (b.value || 0) - (a.value || 0);
      if (sortBy === 'score') return (b.score || 0) - (a.score || 0);
      if (sortBy === 'probability') return (b.probability || 0) - (a.probability || 0);
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
  }, [deals, activeStage, filterPriority, query, sortBy]);

  const pipelineByStage = useMemo(() => {
    const map = {};
    STAGES.forEach(s => { map[s.id] = deals.filter(d => d.stage === s.id); });
    return map;
  }, [deals]);

  const headerOpacity = scrollY.interpolate({ inputRange: [0, 100], outputRange: [1, 0.95], extrapolate: 'clamp' });

  // ── RENDER ──
  if (loading && deals.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={BRAND.blue} />
        <Text style={styles.loadingText}>Loading CRM...</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* ── Header ── */}
      <Animated.View style={[styles.header, { opacity: headerOpacity }]}>
        <LinearGradient colors={[BRAND.blue, '#163b6c']} style={styles.headerGradient}>
          <View style={styles.headerRow}>
            <TouchableOpacity style={styles.headerBtn} onPress={() => navigation?.goBack?.()}>
              <Ionicons name="chevron-back" size={22} color="#fff" />
            </TouchableOpacity>
            <View style={{ flex: 1 }}>
              <Text style={styles.headerTitle}>CRM Dashboard</Text>
              <Text style={styles.headerSubtitle}>
                {deals.filter(d => !['won','lost'].includes(d.stage)).length} active • £{(stats.totalValue / 1000).toFixed(0)}k pipeline • {stats.winRate}% win rate
              </Text>
            </View>
            <View style={styles.headerActions}>
              <TouchableOpacity style={styles.headerBtn} onPress={() => setShowFilters(true)}>
                <Ionicons name="options-outline" size={20} color="#fff" />
              </TouchableOpacity>
              <TouchableOpacity style={styles.headerBtn} onPress={exportPipelineReport}>
                <Ionicons name="share-outline" size={20} color="#fff" />
              </TouchableOpacity>
              <TouchableOpacity style={styles.headerBtn} onPress={() => setShowAddDeal(true)}>
                <Ionicons name="add" size={22} color="#fff" />
              </TouchableOpacity>
            </View>
          </View>

          {/* Tab Bar */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 12, marginBottom: -6 }}>
            {[
              { id: 'dashboard', icon: 'grid-outline', label: 'Dashboard' },
              { id: 'pipeline', icon: 'funnel-outline', label: 'Pipeline' },
              { id: 'contacts', icon: 'people-outline', label: 'Contacts' },
              { id: 'activities', icon: 'calendar-outline', label: 'Activities' },
              { id: 'analytics', icon: 'analytics-outline', label: 'Analytics' },
            ].map(tab => (
              <TouchableOpacity key={tab.id} style={[styles.tab, activeTab === tab.id && styles.tabActive]} onPress={() => setActiveTab(tab.id)}>
                <Ionicons name={tab.icon} size={14} color={activeTab === tab.id ? BRAND.blue : 'rgba(255,255,255,0.7)'} />
                <Text style={[styles.tabText, activeTab === tab.id && { color: BRAND.blue }]}>{tab.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </LinearGradient>
      </Animated.View>

      {error && (
        <View style={styles.errorBanner}>
          <Ionicons name="warning-outline" size={16} color="#fff" />
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity onPress={loadData}><Text style={styles.retryText}>Retry</Text></TouchableOpacity>
        </View>
      )}

      <Animated.ScrollView
        style={{ opacity: fadeAnim }}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={BRAND.blue} colors={[BRAND.blue]} />}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], { useNativeDriver: true })}
        scrollEventThrottle={16}
      >
        <View style={{ height: activeTab === 'dashboard' ? 130 : 126 }} />

        {/* ── DASHBOARD TAB ── */}
        {activeTab === 'dashboard' && (
          <Animated.View style={{ opacity: fadeAnim }}>
            {/* KPI Grid */}
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingLeft: 16, marginBottom: 16 }}>
              {kpis.map(kpi => (
                <View key={kpi.id} style={styles.kpiCard}>
                  <View style={[styles.kpiIconBg, { backgroundColor: kpi.color + '18' }]}>
                    <Ionicons name={kpi.icon} size={18} color={kpi.color} />
                  </View>
                  <Text style={[styles.kpiValue, { color: kpi.color }]}>{kpi.value}</Text>
                  <Text style={styles.kpiLabel}>{kpi.label}</Text>
                  <View style={styles.kpiTrendRow}>
                    <Ionicons name="trending-up" size={10} color={BRAND.success} />
                    <Text style={styles.kpiTrend}>{kpi.trend}</Text>
                  </View>
                </View>
              ))}
              <View style={{ width: 16 }} />
            </ScrollView>

            {/* Quick Stats Row */}
            <View style={styles.statsRow}>
              {[
                { label: 'Month Growth', value: `+${stats.thisMonthGrowth}%`, icon: 'trending-up', color: BRAND.success },
                { label: 'Won / Lost', value: `${stats.wonThisMonth || 3}/${stats.lostThisMonth || 1}`, icon: 'swap-horizontal', color: BRAND.blue },
                { label: 'Avg Cycle', value: `${stats.avgSalesCycle || 21}d`, icon: 'time-outline', color: BRAND.warning },
              ].map((s, i) => (
                <View key={i} style={styles.statPill}>
                  <Ionicons name={s.icon} size={16} color={s.color} />
                  <Text style={[styles.statPillValue, { color: s.color }]}>{s.value}</Text>
                  <Text style={styles.statPillLabel}>{s.label}</Text>
                </View>
              ))}
            </View>

            {/* Search */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={18} color={BRAND.sub} />
              <TextInput
                value={query}
                onChangeText={setQuery}
                placeholder="Search deals, contacts, companies..."
                placeholderTextColor="#94a3b8"
                style={styles.searchInput}
              />
              {query ? (
                <TouchableOpacity onPress={() => setQuery('')}>
                  <Ionicons name="close-circle" size={18} color="#94a3b8" />
                </TouchableOpacity>
              ) : null}
            </View>

            {/* Stage Filter Chips */}
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.stageScroll}>
              {[{ id: 'all', name: 'All', color: BRAND.text }, ...STAGES].map(stage => (
                <TouchableOpacity
                  key={stage.id}
                  style={[styles.stageChip, activeStage === stage.id && { backgroundColor: stage.color + '20', borderColor: stage.color }]}
                  onPress={() => setActiveStage(stage.id)}
                >
                  <View style={[styles.stageDot, { backgroundColor: stage.color }]} />
                  <Text style={[styles.stageChipText, activeStage === stage.id && { color: stage.color }]}>
                    {stage.name} ({stage.id === 'all' ? deals.length : deals.filter(d => d.stage === stage.id).length})
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            {/* Quick Actions Grid */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Quick Actions</Text>
              <View style={styles.actionsGrid}>
                {[
                  { label: 'Pipeline View', icon: 'funnel', color: BRAND.blue, onPress: () => setShowPipeline(true) },
                  { label: 'AI Advisor', icon: 'sparkles', color: '#7c3aed', onPress: () => setShowAIAdvisor(true) },
                  { label: 'Analytics', icon: 'bar-chart', color: BRAND.mint, onPress: () => setShowAnalytics(true) },
                  { label: 'Forecast', icon: 'trending-up', color: BRAND.success, onPress: () => setShowForecast(true) },
                  { label: 'Contacts', icon: 'people', color: BRAND.canary, onPress: () => setShowContacts(true) },
                  { label: 'Email Templates', icon: 'mail', color: BRAND.warning, onPress: () => setShowEmailTemplates(true) },
                  { label: 'Team', icon: 'person-circle', color: BRAND.info, onPress: () => setShowTeam(true) },
                  { label: 'Add Activity', icon: 'calendar', color: BRAND.danger, onPress: () => setShowAddActivity(true) },
                  { label: 'Add Deal', icon: 'add-circle', color: BRAND.blue, onPress: () => setShowAddDeal(true) },
                  { label: 'Export Report', icon: 'share-social', color: BRAND.sub, onPress: exportPipelineReport },
                  { label: 'ScaleUp Hub', icon: 'rocket', color: '#8b5cf6', onPress: () => navigation?.navigate?.('ScaleUpBusinessScreen') },
                  { label: 'ML Platform', icon: 'hardware-chip-outline', color: '#667eea', onPress: () => navigation?.navigate?.('MachineLearningScreen') },
                ].map((action, i) => (
                  <TouchableOpacity key={i} style={styles.actionBtn} onPress={action.onPress} activeOpacity={0.7}>
                    <View style={[styles.actionBtnIcon, { backgroundColor: action.color + '18' }]}>
                      <Ionicons name={action.icon} size={22} color={action.color} />
                    </View>
                    <Text style={styles.actionBtnLabel} numberOfLines={2}>{action.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Deals List */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>
                  {activeStage === 'all' ? 'All Deals' : STAGES.find(s => s.id === activeStage)?.name + ' Deals'} ({filteredDeals.length})
                </Text>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <TouchableOpacity style={styles.sortBtn} onPress={() => {
                    const opts = ['updated_at', 'value', 'score', 'probability'];
                    const idx = opts.indexOf(sortBy);
                    setSortBy(opts[(idx + 1) % opts.length]);
                  }}>
                    <Ionicons name="swap-vertical" size={14} color={BRAND.blue} />
                    <Text style={styles.sortBtnText}>{sortBy === 'updated_at' ? 'Date' : sortBy === 'value' ? 'Value' : sortBy === 'score' ? 'Score' : 'Prob%'}</Text>
                  </TouchableOpacity>
                </View>
              </View>

              {filteredDeals.length === 0 ? (
                <View style={styles.emptyState}>
                  <Ionicons name="briefcase-outline" size={48} color={BRAND.line} />
                  <Text style={styles.emptyTitle}>No deals found</Text>
                  <Text style={styles.emptyText}>Adjust filters or create a new deal</Text>
                  <TouchableOpacity style={styles.emptyButton} onPress={() => setShowAddDeal(true)}>
                    <Text style={styles.emptyButtonText}>+ Create Deal</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                filteredDeals.map(deal => (
                  <DealCard
                    key={deal.id}
                    deal={deal}
                    onPress={() => { setSelectedDeal(deal); setShowDealDetail(true); }}
                    onStageChange={(s) => handleStageChange(deal.id, s)}
                    onDelete={() => deleteDeal(deal.id)}
                  />
                ))
              )}
            </View>

            {/* Today's Activities */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Today's Activities ({activities.length})</Text>
                <TouchableOpacity style={styles.smallBtn} onPress={() => setShowAddActivity(true)}>
                  <Ionicons name="add" size={14} color={BRAND.blue} />
                  <Text style={styles.smallBtnText}>Add</Text>
                </TouchableOpacity>
              </View>
              {activities.length === 0 ? (
                <View style={styles.emptyState}>
                  <Ionicons name="calendar-outline" size={36} color={BRAND.line} />
                  <Text style={styles.emptyText}>No activities scheduled for today</Text>
                </View>
              ) : (
                activities.map(activity => (
                  <ActivityItem
                    key={activity.id}
                    activity={activity}
                    onToggleComplete={() => toggleActivity(activity.id, activity.completed)}
                    onEmailTemplate={() => { setShowEmailTemplates(true); }}
                  />
                ))
              )}
            </View>

            {/* Integration Widget */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Connected Platforms</Text>
              <View style={styles.integrationRow}>
                <TouchableOpacity style={[styles.integrationCard, { borderColor: '#8b5cf6' + '40' }]} onPress={() => navigation?.navigate?.('ScaleUpBusinessScreen')}>
                  <LinearGradient colors={['#8b5cf620', '#7c3aed10']} style={styles.integrationGrad}>
                    <Ionicons name="rocket-outline" size={24} color="#8b5cf6" />
                    <Text style={[styles.integrationTitle, { color: '#8b5cf6' }]}>Scale-Up Hub</Text>
                    <Text style={styles.integrationSub}>Growth strategy & OKRs</Text>
                  </LinearGradient>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.integrationCard, { borderColor: '#667eea40' }]} onPress={() => navigation?.navigate?.('MachineLearningScreen')}>
                  <LinearGradient colors={['#667eea20', '#764ba210']} style={styles.integrationGrad}>
                    <Ionicons name="hardware-chip-outline" size={24} color="#667eea" />
                    <Text style={[styles.integrationTitle, { color: '#667eea' }]}>ML Platform</Text>
                    <Text style={styles.integrationSub}>AI lead scoring & insights</Text>
                  </LinearGradient>
                </TouchableOpacity>
              </View>
            </View>
          </Animated.View>
        )}

        {/* ── PIPELINE TAB ── */}
        {activeTab === 'pipeline' && (
          <View style={{ paddingTop: 12 }}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingLeft: 16 }}>
              {STAGES.map(stage => {
                const stageDeals = pipelineByStage[stage.id] || [];
                const stageValue = stageDeals.reduce((s, d) => s + (d.value || 0), 0);
                return (
                  <View key={stage.id} style={styles.pipelineCol}>
                    <View style={[styles.pipelineColHeader, { backgroundColor: stage.color }]}>
                      <Ionicons name={stage.icon} size={14} color="#fff" />
                      <Text style={styles.pipelineColTitle}>{stage.name}</Text>
                      <View style={styles.pipelineCount}>
                        <Text style={styles.pipelineCountText}>{stageDeals.length}</Text>
                      </View>
                    </View>
                    <Text style={[styles.pipelineValue, { color: stage.color }]}>£{stageValue.toLocaleString()}</Text>
                    {stageDeals.map(deal => (
                      <TouchableOpacity
                        key={deal.id}
                        style={styles.pipelineCard}
                        onPress={() => { setSelectedDeal(deal); setShowDealDetail(true); }}
                        activeOpacity={0.7}
                      >
                        <Text style={styles.pipelineCardCompany} numberOfLines={1}>{deal.company}</Text>
                        <Text style={styles.pipelineCardContact} numberOfLines={1}>{deal.contact_name}</Text>
                        <View style={styles.pipelineCardFooter}>
                          <Text style={[styles.pipelineCardValue, { color: stage.color }]}>£{deal.value?.toLocaleString()}</Text>
                          <View style={[styles.pipelineScore, { backgroundColor: deal.score >= 70 ? BRAND.success + '25' : BRAND.warning + '25' }]}>
                            <Text style={[styles.pipelineScoreText, { color: deal.score >= 70 ? BRAND.success : BRAND.warning }]}>{deal.score}</Text>
                          </View>
                        </View>
                        <View style={styles.probBarOuter}>
                          <View style={[styles.probBarInner, { width: `${deal.probability || 0}%`, backgroundColor: stage.color }]} />
                        </View>
                        <Text style={styles.pipelineProbText}>{deal.probability}%</Text>
                      </TouchableOpacity>
                    ))}
                    {stageDeals.length === 0 && (
                      <View style={styles.pipelineEmpty}>
                        <Text style={styles.pipelineEmptyText}>No deals</Text>
                      </View>
                    )}
                  </View>
                );
              })}
              <View style={{ width: 16 }} />
            </ScrollView>
          </View>
        )}

        {/* ── CONTACTS TAB ── */}
        {activeTab === 'contacts' && (
          <View style={{ paddingTop: 12 }}>
            <View style={[styles.searchContainer, { marginHorizontal: 16, marginBottom: 8 }]}>
              <Ionicons name="search" size={18} color={BRAND.sub} />
              <TextInput
                placeholder="Search contacts..."
                placeholderTextColor="#94a3b8"
                style={styles.searchInput}
              />
            </View>
            <View style={{ paddingHorizontal: 16 }}>
              <View style={[styles.sectionHeader, { marginBottom: 12 }]}>
                <Text style={styles.sectionTitle}>All Contacts ({contacts.length})</Text>
                <TouchableOpacity style={styles.smallBtn} onPress={() => setShowAddContact(true)}>
                  <Ionicons name="add" size={14} color={BRAND.blue} />
                  <Text style={styles.smallBtnText}>Add</Text>
                </TouchableOpacity>
              </View>
              {contacts.map(contact => (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  onPress={() => { setSelectedContact(contact); setShowContactDetail(true); }}
                />
              ))}
            </View>
          </View>
        )}

        {/* ── ACTIVITIES TAB ── */}
        {activeTab === 'activities' && (
          <View style={{ paddingTop: 12, paddingHorizontal: 16 }}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>All Activities</Text>
              <TouchableOpacity style={styles.smallBtn} onPress={() => setShowAddActivity(true)}>
                <Ionicons name="add" size={14} color={BRAND.blue} />
                <Text style={styles.smallBtnText}>Schedule</Text>
              </TouchableOpacity>
            </View>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 12 }}>
              {ACTIVITY_TYPES.map(t => (
                <View key={t.id} style={[styles.actTypeChip, { borderColor: t.color + '60', backgroundColor: t.color + '15' }]}>
                  <Ionicons name={t.icon} size={13} color={t.color} />
                  <Text style={[styles.actTypeText, { color: t.color }]}>{t.name}</Text>
                </View>
              ))}
            </ScrollView>
            {ACTIVITY_TYPES.map(type => {
              const typeActivities = activities.filter(a => a.type === type.id);
              if (typeActivities.length === 0) return null;
              return (
                <View key={type.id} style={{ marginBottom: 12 }}>
                  <View style={styles.actTypeHeader}>
                    <Ionicons name={type.icon} size={15} color={type.color} />
                    <Text style={[styles.actTypeHeaderText, { color: type.color }]}>{type.name}s ({typeActivities.length})</Text>
                  </View>
                  {typeActivities.map(activity => (
                    <ActivityItem
                      key={activity.id}
                      activity={activity}
                      onToggleComplete={() => toggleActivity(activity.id, activity.completed)}
                    />
                  ))}
                </View>
              );
            })}
          </View>
        )}

        {/* ── ANALYTICS TAB ── */}
        {activeTab === 'analytics' && (
          <View style={{ paddingTop: 12, paddingHorizontal: 16 }}>
            <Text style={[styles.sectionTitle, { marginBottom: 16 }]}>Sales Analytics</Text>

            {/* Stage Funnel */}
            <View style={styles.analyticsCard}>
              <Text style={styles.analyticsCardTitle}>Pipeline Funnel</Text>
              {STAGES.filter(s => s.id !== 'lost').map(stage => {
                const count = deals.filter(d => d.stage === stage.id).length;
                const maxCount = Math.max(...STAGES.map(s => deals.filter(d => d.stage === s.id).length), 1);
                const barW = (count / maxCount) * (width - 120);
                return (
                  <View key={stage.id} style={styles.funnelRow}>
                    <Text style={styles.funnelLabel}>{stage.name}</Text>
                    <View style={styles.funnelBarOuter}>
                      <View style={[styles.funnelBarInner, { width: Math.max(barW, 4), backgroundColor: stage.color }]} />
                    </View>
                    <Text style={[styles.funnelCount, { color: stage.color }]}>{count}</Text>
                  </View>
                );
              })}
            </View>

            {/* Revenue by Source */}
            <View style={styles.analyticsCard}>
              <Text style={styles.analyticsCardTitle}>Deals by Source</Text>
              {DEAL_SOURCES.map(src => {
                const srcDeals = deals.filter(d => d.source === src.id);
                const srcValue = srcDeals.reduce((s, d) => s + (d.value || 0), 0);
                if (srcValue === 0) return null;
                const maxVal = Math.max(...DEAL_SOURCES.map(s => deals.filter(d => d.source === s.id).reduce((sum, d) => sum + (d.value || 0), 0)), 1);
                return (
                  <View key={src.id} style={styles.funnelRow}>
                    <Text style={styles.funnelLabel}>{src.name}</Text>
                    <View style={styles.funnelBarOuter}>
                      <View style={[styles.funnelBarInner, { width: Math.max((srcValue / maxVal) * (width - 130), 4), backgroundColor: BRAND.mint }]} />
                    </View>
                    <Text style={styles.funnelCount}>£{(srcValue / 1000).toFixed(0)}k</Text>
                  </View>
                );
              })}
            </View>

            {/* Team Performance */}
            <View style={styles.analyticsCard}>
              <Text style={styles.analyticsCardTitle}>Team Leaderboard</Text>
              {users.sort((a, b) => (b.revenue || 0) - (a.revenue || 0)).map((user, i) => (
                <View key={user.id} style={styles.leaderRow}>
                  <View style={[styles.leaderRank, { backgroundColor: i === 0 ? BRAND.canary : i === 1 ? '#94a3b8' : '#cd7f32' }]}>
                    <Text style={styles.leaderRankText}>{i + 1}</Text>
                  </View>
                  <View style={[styles.leaderAvatar, { backgroundColor: BRAND.blue + '20' }]}>
                    <Text style={[styles.leaderAvatarText, { color: BRAND.blue }]}>{user.avatar || user.name?.substring(0, 2)}</Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.leaderName}>{user.name}</Text>
                    <Text style={styles.leaderSub}>{user.deals} deals • {user.winRate}% win rate</Text>
                  </View>
                  <Text style={[styles.leaderRevenue, { color: BRAND.success }]}>£{(user.revenue / 1000).toFixed(0)}k</Text>
                </View>
              ))}
            </View>

            {/* Forecast Bars */}
            <View style={styles.analyticsCard}>
              <Text style={styles.analyticsCardTitle}>Revenue Forecast (6 months)</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.barChartContainer}>
                  {forecastData.map((m, i) => {
                    const maxRev = Math.max(...forecastData.map(x => Math.max(x.revenue, x.target)), 1);
                    const revH = Math.max((m.revenue / maxRev) * 140, 4);
                    const tarH = Math.max((m.target / maxRev) * 140, 4);
                    return (
                      <View key={i} style={styles.barGroup}>
                        <View style={styles.barsRow}>
                          <View style={[styles.bar, { height: tarH, backgroundColor: BRAND.line, marginRight: 2 }]} />
                          <View style={[styles.bar, { height: revH, backgroundColor: m.revenue >= m.target ? BRAND.success : BRAND.blue }]} />
                        </View>
                        <Text style={styles.barLabel}>{m.month}</Text>
                      </View>
                    );
                  })}
                </View>
              </ScrollView>
              <View style={styles.barLegend}>
                <View style={styles.legendItem}><View style={[styles.legendDot, { backgroundColor: BRAND.blue }]} /><Text style={styles.legendText}>Actual</Text></View>
                <View style={styles.legendItem}><View style={[styles.legendDot, { backgroundColor: BRAND.line }]} /><Text style={styles.legendText}>Target</Text></View>
              </View>
            </View>
          </View>
        )}

        <View style={{ height: 120 }} />
      </Animated.ScrollView>

      {/* ── FAB ── */}
      <TouchableOpacity style={styles.fab} onPress={() => setShowAddDeal(true)} activeOpacity={0.9}>
        <Animated.View style={{ transform: [{ scale: pulse.interpolate({ inputRange: [0,1], outputRange: [1, 1.08] }) }] }}>
          <LinearGradient colors={[BRAND.blue, BRAND.mint]} style={styles.fabInner}>
            <Ionicons name="add" size={26} color="#fff" />
          </LinearGradient>
        </Animated.View>
      </TouchableOpacity>

      {/* ── MODALS ── */}
      <AddDealModal visible={showAddDeal} onClose={() => setShowAddDeal(false)} form={dealForm} setForm={setDealForm}
        onSubmit={async () => { await createDeal({ ...dealForm, value: parseInt(dealForm.value) || 0, due_date: dealForm.due_date.toISOString() }); setShowAddDeal(false); setDealForm({ company:'',contact_name:'',contact_email:'',contact_phone:'',stage:'lead',value:'',probability:30,priority:'medium',next_action:'',due_date:new Date(),tags:[],notes:'',source:'direct',assigned_to:null }); }}
        clients={contacts} users={users} />

      <AddActivityModal visible={showAddActivity} onClose={() => setShowAddActivity(false)} form={activityForm} setForm={setActivityForm}
        onSubmit={async () => { await createActivity({ ...activityForm, scheduled_at: activityForm.scheduled_at.toISOString() }); setShowAddActivity(false); setActivityForm({ type:'call',title:'',description:'',deal_id:null,client_id:null,scheduled_at:new Date(),duration:30,notes:'',completed:false }); }}
        deals={deals} clients={contacts} />

      <DealDetailModal visible={showDealDetail} deal={selectedDeal} onClose={() => setShowDealDetail(false)}
        onUpdate={updateDeal} onDelete={deleteDeal} onStageChange={handleStageChange}
        notes={selectedDeal ? (notes[selectedDeal.id] || []) : []}
        newNote={newNote} setNewNote={setNewNote}
        onAddNote={() => addNoteToDeal(selectedDeal?.id, newNote)}
        onEmailTemplate={() => { setShowDealDetail(false); setShowEmailTemplates(true); }}
        onAddActivity={() => { setShowDealDetail(false); setShowAddActivity(true); }} />

      <ContactsModal visible={showContacts} contacts={contacts} onClose={() => setShowContacts(false)}
        onSelect={(c) => { setSelectedContact(c); setShowContacts(false); setShowContactDetail(true); }}
        onAdd={() => { setShowContacts(false); setShowAddContact(true); }} />

      <AddContactModal visible={showAddContact} onClose={() => setShowAddContact(false)}
        form={contactForm} setForm={setContactForm}
        onSubmit={async () => { await createContact(contactForm); setShowAddContact(false); setContactForm({ name:'',company:'',email:'',phone:'',role:'',linkedin:'',notes:'' }); }} />

      <ContactDetailModal visible={showContactDetail} contact={selectedContact} deals={deals} onClose={() => setShowContactDetail(false)}
        onEmail={() => { setShowContactDetail(false); setShowEmailTemplates(true); }} />

      <AnalyticsModal visible={showAnalytics} deals={deals} stats={stats} users={users} forecastData={forecastData} onClose={() => setShowAnalytics(false)} />

      <ForecastModal visible={showForecast} forecastData={forecastData} stats={stats} deals={deals} onClose={() => setShowForecast(false)} />

      <AIAdvisorModal visible={showAIAdvisor} onClose={() => setShowAIAdvisor(false)}
        aiHistory={aiHistory} aiLoading={aiLoading} aiQuery={aiQuery} setAiQuery={setAiQuery}
        onAsk={handleAskAI} deals={deals} stats={stats} />

      <EmailTemplatesModal visible={showEmailTemplates} templates={EMAIL_TEMPLATES} deal={selectedDeal}
        onClose={() => setShowEmailTemplates(false)} />

      <TeamModal visible={showTeam} users={users} deals={deals} onClose={() => setShowTeam(false)} />

      <PipelineKanbanModal visible={showPipeline} deals={deals} onClose={() => setShowPipeline(false)}
        onDealPress={(deal) => { setSelectedDeal(deal); setShowPipeline(false); setShowDealDetail(true); }}
        onStageChange={handleStageChange} />

      <FiltersModal visible={showFilters} onClose={() => setShowFilters(false)}
        filterPriority={filterPriority} setFilterPriority={setFilterPriority}
        sortBy={sortBy} setSortBy={setSortBy} />
    </SafeAreaView>
  );
}

// ─── DEAL CARD ─────────────────────────────────────────────────────────────
function DealCard({ deal, onPress, onStageChange, onDelete }) {
  const stage = STAGES.find(s => s.id === deal.stage) || STAGES[0];
  const priority = PRIORITIES.find(p => p.id === deal.priority) || PRIORITIES[1];
  const scoreColor = deal.score >= 75 ? BRAND.success : deal.score >= 50 ? BRAND.warning : BRAND.danger;

  return (
    <TouchableOpacity style={styles.dealCard} onPress={onPress} activeOpacity={0.75}>
      <View style={[styles.dealScoreBar, { backgroundColor: scoreColor }]} />
      <View style={styles.dealCardInner}>
        <View style={styles.dealHeader}>
          <View style={{ flex: 1 }}>
            <Text style={styles.dealCompany} numberOfLines={1}>{deal.company}</Text>
            <Text style={styles.dealContact} numberOfLines={1}>{deal.contact_name} • {deal.contact_email}</Text>
            {deal.assigned_user && <Text style={styles.assignedTo}>📌 {deal.assigned_user.name}</Text>}
          </View>
          <View style={{ alignItems: 'flex-end', gap: 4 }}>
            <Text style={[styles.dealValue, { color: BRAND.success }]}>£{deal.value?.toLocaleString()}</Text>
            <View style={[styles.scoreBadge, { backgroundColor: scoreColor + '20' }]}>
              <Ionicons name="star" size={9} color={scoreColor} />
              <Text style={[styles.scoreText, { color: scoreColor }]}>{deal.score}</Text>
            </View>
          </View>
        </View>

        <View style={styles.dealBadges}>
          <View style={[styles.stageBadge, { backgroundColor: stage.color + '20', borderColor: stage.color }]}>
            <Ionicons name={stage.icon} size={10} color={stage.color} />
            <Text style={[styles.stageText, { color: stage.color }]}>{stage.name}</Text>
          </View>
          <View style={[styles.priorityBadge, { backgroundColor: priority.color + '20' }]}>
            <Text style={[styles.priorityText, { color: priority.color }]}>{priority.name}</Text>
          </View>
          {deal.source && (
            <View style={styles.sourceBadge}>
              <Text style={styles.sourceText}>{DEAL_SOURCES.find(s => s.id === deal.source)?.name || deal.source}</Text>
            </View>
          )}
        </View>

        <View style={styles.probRow}>
          <View style={styles.probBarOuter}>
            <View style={[styles.probBarInner, { width: `${deal.probability || 0}%`, backgroundColor: (deal.probability || 0) > 70 ? BRAND.success : (deal.probability || 0) > 40 ? BRAND.warning : BRAND.danger }]} />
          </View>
          <Text style={styles.probText}>{deal.probability}%</Text>
        </View>

        {deal.next_action && (
          <View style={styles.nextAction}>
            <Ionicons name="arrow-forward-circle" size={13} color={BRAND.blue} />
            <Text style={styles.nextActionText} numberOfLines={1}>{deal.next_action}</Text>
          </View>
        )}

        {deal.tags?.length > 0 && (
          <View style={styles.tagsRow}>
            {deal.tags.slice(0, 4).map((tag, i) => (
              <View key={i} style={styles.tag}><Text style={styles.tagText}>{tag}</Text></View>
            ))}
            {deal.tags.length > 4 && <Text style={styles.moreTags}>+{deal.tags.length - 4}</Text>}
          </View>
        )}

        <View style={styles.dealFooterRow}>
          {deal.due_date && (
            <View style={styles.dueDateBadge}>
              <Ionicons name="calendar-outline" size={11} color={BRAND.sub} />
              <Text style={styles.dueDateText}>Due {new Date(deal.due_date).toLocaleDateString()}</Text>
            </View>
          )}
          <View style={styles.dealActions}>
            <TouchableOpacity style={styles.actionIcon} onPress={() => onStageChange('won')}>
              <Ionicons name="checkmark-circle" size={20} color={BRAND.success} />
            </TouchableOpacity>
            <TouchableOpacity style={styles.actionIcon} onPress={onDelete}>
              <Ionicons name="trash-outline" size={18} color={BRAND.danger} />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
}

// ─── ACTIVITY ITEM ─────────────────────────────────────────────────────────
function ActivityItem({ activity, onToggleComplete, onEmailTemplate }) {
  const type = ACTIVITY_TYPES.find(t => t.id === activity.type) || ACTIVITY_TYPES[0];
  return (
    <View style={[styles.activityItem, activity.completed && styles.activityDone]}>
      <TouchableOpacity style={[styles.activityIconBg, { backgroundColor: type.color + '20' }]} onPress={onToggleComplete}>
        <Ionicons name={activity.completed ? 'checkmark-circle' : type.icon} size={18} color={activity.completed ? BRAND.success : type.color} />
      </TouchableOpacity>
      <View style={{ flex: 1 }}>
        <Text style={[styles.activityTitle, activity.completed && { textDecorationLine: 'line-through', color: BRAND.sub }]}>
          {activity.title}
        </Text>
        {activity.description ? <Text style={styles.activityDesc} numberOfLines={1}>{activity.description}</Text> : null}
        {activity.deal && <Text style={styles.activityMeta}>🏢 {activity.deal.company}</Text>}
      </View>
      <View style={styles.activityRight}>
        <Text style={styles.activityTime}>{new Date(activity.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Text>
        <View style={[styles.actTypeBadge, { backgroundColor: type.color + '20' }]}>
          <Text style={[styles.actTypeBadgeText, { color: type.color }]}>{type.name}</Text>
        </View>
      </View>
    </View>
  );
}

// ─── CONTACT CARD ──────────────────────────────────────────────────────────
function ContactCard({ contact, onPress }) {
  const scoreColor = contact.score >= 70 ? BRAND.success : contact.score >= 40 ? BRAND.warning : BRAND.danger;
  return (
    <TouchableOpacity style={styles.contactCard} onPress={onPress} activeOpacity={0.75}>
      <View style={[styles.contactAvatar, { backgroundColor: BRAND.blue + '20' }]}>
        <Text style={styles.contactAvatarText}>{contact.name?.substring(0, 2).toUpperCase()}</Text>
      </View>
      <View style={{ flex: 1 }}>
        <Text style={styles.contactName}>{contact.name}</Text>
        <Text style={styles.contactRole}>{contact.role} • {contact.company}</Text>
        <Text style={styles.contactEmail} numberOfLines={1}>{contact.email}</Text>
      </View>
      <View style={{ alignItems: 'flex-end', gap: 4 }}>
        <View style={[styles.scoreBadge, { backgroundColor: scoreColor + '20' }]}>
          <Text style={[styles.scoreText, { color: scoreColor }]}>{contact.score}</Text>
        </View>
        {contact.deals?.length > 0 && (
          <View style={styles.dealsBadge}>
            <Text style={styles.dealsBadgeText}>{contact.deals.length} deal{contact.deals.length !== 1 ? 's' : ''}</Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
}

// ─── ADD DEAL MODAL ────────────────────────────────────────────────────────
function AddDealModal({ visible, onClose, form, setForm, onSubmit, clients, users }) {
  const [loading, setLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const addTag = () => { if (tagInput.trim()) { set('tags', [...(form.tags || []), tagInput.trim()]); setTagInput(''); } };
  const removeTag = (i) => set('tags', form.tags.filter((_, idx) => idx !== i));

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <LinearGradient colors={[BRAND.blue, '#163b6c']} style={styles.modalHeaderGrad}>
            <TouchableOpacity onPress={onClose} style={styles.modalCloseBtn}>
              <Ionicons name="close" size={22} color="#fff" />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>New Deal</Text>
            <TouchableOpacity style={styles.modalSubmitBtn} onPress={async () => { setLoading(true); await onSubmit(); setLoading(false); }}>
              {loading ? <ActivityIndicator size="small" color="#fff" /> : <Text style={styles.modalSubmitText}>Create</Text>}
            </TouchableOpacity>
          </LinearGradient>
        </View>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1 }}>
          <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
            <Text style={styles.modalSection}>Company & Contact</Text>
            <TextInput style={styles.modalInput} placeholder="Company Name *" value={form.company} onChangeText={v => set('company', v)} placeholderTextColor="#94a3b8" />
            <TextInput style={styles.modalInput} placeholder="Contact Name" value={form.contact_name} onChangeText={v => set('contact_name', v)} placeholderTextColor="#94a3b8" />
            <TextInput style={styles.modalInput} placeholder="Contact Email" value={form.contact_email} onChangeText={v => set('contact_email', v)} placeholderTextColor="#94a3b8" keyboardType="email-address" autoCapitalize="none" />
            <TextInput style={styles.modalInput} placeholder="Contact Phone" value={form.contact_phone} onChangeText={v => set('contact_phone', v)} placeholderTextColor="#94a3b8" keyboardType="phone-pad" />

            <Text style={styles.modalSection}>Deal Details</Text>
            <TextInput style={styles.modalInput} placeholder="Deal Value (£)" value={form.value} onChangeText={v => set('value', v)} placeholderTextColor="#94a3b8" keyboardType="numeric" />
            <View style={styles.modalRow}>
              <Text style={styles.modalLabel}>Probability: {form.probability}%</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {[10,20,30,40,50,60,70,80,90,100].map(p => (
                  <TouchableOpacity key={p} style={[styles.probChip, form.probability === p && styles.probChipActive]} onPress={() => set('probability', p)}>
                    <Text style={[styles.probChipText, form.probability === p && styles.probChipTextActive]}>{p}%</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>

            <Text style={styles.modalLabel}>Stage</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 12 }}>
              {STAGES.map(s => (
                <TouchableOpacity key={s.id} style={[styles.stageSelectChip, form.stage === s.id && { backgroundColor: s.color + '25', borderColor: s.color }]} onPress={() => set('stage', s.id)}>
                  <Text style={[styles.stageSelectText, form.stage === s.id && { color: s.color }]}>{s.name}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <Text style={styles.modalLabel}>Priority</Text>
            <View style={styles.priorityRow}>
              {PRIORITIES.map(p => (
                <TouchableOpacity key={p.id} style={[styles.priorityChip, form.priority === p.id && { backgroundColor: p.color + '25', borderColor: p.color }]} onPress={() => set('priority', p.id)}>
                  <Text style={[styles.priorityChipText, form.priority === p.id && { color: p.color }]}>{p.name}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={styles.modalLabel}>Source</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 12 }}>
              {DEAL_SOURCES.map(src => (
                <TouchableOpacity key={src.id} style={[styles.sourceChip, form.source === src.id && styles.sourceChipActive]} onPress={() => set('source', src.id)}>
                  <Text style={[styles.sourceChipText, form.source === src.id && styles.sourceChipTextActive]}>{src.name}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <Text style={styles.modalSection}>Next Steps</Text>
            <TextInput style={styles.modalInput} placeholder="Next Action Required" value={form.next_action} onChangeText={v => set('next_action', v)} placeholderTextColor="#94a3b8" />
            <TouchableOpacity style={styles.datePickerBtn} onPress={() => setShowDatePicker(true)}>
              <Ionicons name="calendar-outline" size={18} color={BRAND.blue} />
              <Text style={styles.datePickerText}>Due Date: {form.due_date.toLocaleDateString()}</Text>
            </TouchableOpacity>
            {showDatePicker && (
              <View style={styles.datePickerWrapper}>
                <View style={{ flexDirection: 'row', justifyContent: 'flex-end', paddingHorizontal: 16 }}>
                  <TouchableOpacity onPress={() => setShowDatePicker(false)} style={styles.datePickerDone}>
                    <Text style={{ color: BRAND.blue, fontWeight: 'bold' }}>Done</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}

            <Text style={styles.modalSection}>Tags</Text>
            <View style={styles.tagInputRow}>
              <TextInput style={[styles.modalInput, { flex: 1, marginBottom: 0 }]} placeholder="Add tag..." value={tagInput} onChangeText={setTagInput} placeholderTextColor="#94a3b8" onSubmitEditing={addTag} />
              <TouchableOpacity style={styles.addTagBtn} onPress={addTag}>
                <Ionicons name="add" size={18} color={BRAND.blue} />
              </TouchableOpacity>
            </View>
            {form.tags?.length > 0 && (
              <View style={[styles.tagsRow, { marginBottom: 12 }]}>
                {form.tags.map((tag, i) => (
                  <TouchableOpacity key={i} style={[styles.tag, { backgroundColor: BRAND.blue + '20', flexDirection: 'row', gap: 4 }]} onPress={() => removeTag(i)}>
                    <Text style={[styles.tagText, { color: BRAND.blue }]}>{tag}</Text>
                    <Ionicons name="close" size={10} color={BRAND.blue} />
                  </TouchableOpacity>
                ))}
              </View>
            )}

            <Text style={styles.modalLabel}>Notes</Text>
            <TextInput style={[styles.modalInput, { height: 80, textAlignVertical: 'top' }]} placeholder="Internal notes..." value={form.notes} onChangeText={v => set('notes', v)} placeholderTextColor="#94a3b8" multiline numberOfLines={4} />
            <View style={{ height: 40 }} />
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );
}

// ─── ADD ACTIVITY MODAL ────────────────────────────────────────────────────
function AddActivityModal({ visible, onClose, form, setForm, onSubmit, deals, clients }) {
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <LinearGradient colors={[BRAND.mint, '#147a82']} style={styles.modalHeaderGrad}>
            <TouchableOpacity onPress={onClose} style={styles.modalCloseBtn}>
              <Ionicons name="close" size={22} color="#fff" />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Schedule Activity</Text>
            <TouchableOpacity style={styles.modalSubmitBtn} onPress={async () => { setLoading(true); await onSubmit(); setLoading(false); }}>
              {loading ? <ActivityIndicator size="small" color="#fff" /> : <Text style={styles.modalSubmitText}>Save</Text>}
            </TouchableOpacity>
          </LinearGradient>
        </View>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1 }}>
          <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
            <Text style={styles.modalLabel}>Activity Type</Text>
            <View style={styles.actTypesGrid}>
              {ACTIVITY_TYPES.map(t => (
                <TouchableOpacity key={t.id} style={[styles.actTypeBtn, form.type === t.id && { backgroundColor: t.color + '25', borderColor: t.color }]} onPress={() => set('type', t.id)}>
                  <Ionicons name={t.icon} size={18} color={form.type === t.id ? t.color : BRAND.sub} />
                  <Text style={[styles.actTypeBtnText, form.type === t.id && { color: t.color }]}>{t.name}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TextInput style={styles.modalInput} placeholder="Activity Title *" value={form.title} onChangeText={v => set('title', v)} placeholderTextColor="#94a3b8" />
            <TextInput style={[styles.modalInput, { height: 70, textAlignVertical: 'top' }]} placeholder="Description (optional)" value={form.description} onChangeText={v => set('description', v)} placeholderTextColor="#94a3b8" multiline />
            <Text style={styles.modalLabel}>Link to Deal</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 12 }}>
              {[{ id: null, company: 'No deal' }, ...deals.slice(0, 10)].map(d => (
                <TouchableOpacity key={d.id || 'none'} style={[styles.dealLinkChip, form.deal_id === d.id && styles.dealLinkChipActive]} onPress={() => set('deal_id', d.id)}>
                  <Text style={[styles.dealLinkText, form.deal_id === d.id && styles.dealLinkTextActive]} numberOfLines={1}>{d.company}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <Text style={styles.modalLabel}>Duration (minutes)</Text>
            <View style={styles.durationRow}>
              {[15, 30, 45, 60, 90, 120].map(d => (
                <TouchableOpacity key={d} style={[styles.durationChip, form.duration === d && styles.durationChipActive]} onPress={() => set('duration', d)}>
                  <Text style={[styles.durationText, form.duration === d && styles.durationTextActive]}>{d}m</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TextInput style={[styles.modalInput, { height: 60, textAlignVertical: 'top' }]} placeholder="Notes..." value={form.notes} onChangeText={v => set('notes', v)} placeholderTextColor="#94a3b8" multiline />
            <View style={{ height: 40 }} />
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );
}

// ─── DEAL DETAIL MODAL ─────────────────────────────────────────────────────
function DealDetailModal({ visible, deal, onClose, onUpdate, onDelete, onStageChange, notes, newNote, setNewNote, onAddNote, onEmailTemplate, onAddActivity }) {
  const [editingStage, setEditingStage] = useState(false);
  if (!deal) return null;
  const stage = STAGES.find(s => s.id === deal.stage) || STAGES[0];
  const scoreColor = deal.score >= 75 ? BRAND.success : deal.score >= 50 ? BRAND.warning : BRAND.danger;

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <LinearGradient colors={[stage.color, stage.color + 'cc']} style={styles.modalHeaderGrad}>
            <TouchableOpacity onPress={onClose} style={styles.modalCloseBtn}>
              <Ionicons name="close" size={22} color="#fff" />
            </TouchableOpacity>
            <View style={{ flex: 1 }}>
              <Text style={styles.modalTitle} numberOfLines={1}>{deal.company}</Text>
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>{stage.name} · £{deal.value?.toLocaleString()}</Text>
            </View>
            <View style={[styles.scoreBadgeLg, { backgroundColor: scoreColor }]}>
              <Text style={styles.scoreBadgeLgText}>{deal.score}</Text>
            </View>
          </LinearGradient>
        </View>
        <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
          {/* Contact Info */}
          <View style={styles.detailSection}>
            <Text style={styles.detailSectionTitle}>Contact Information</Text>
            <View style={styles.detailCard}>
              <DetailRow icon="person-outline" label="Contact" value={deal.contact_name} />
              <DetailRow icon="mail-outline" label="Email" value={deal.contact_email} />
              <DetailRow icon="call-outline" label="Phone" value={deal.contact_phone || 'Not provided'} />
              <DetailRow icon="person-circle-outline" label="Assigned To" value={deal.assigned_user?.name || 'Unassigned'} />
            </View>
          </View>

          {/* Deal Info */}
          <View style={styles.detailSection}>
            <Text style={styles.detailSectionTitle}>Deal Information</Text>
            <View style={styles.detailCard}>
              <DetailRow icon="cash" label="Value" value={`£${deal.value?.toLocaleString()}`} valueColor={BRAND.success} />
              <DetailRow icon="analytics-outline" label="Probability" value={`${deal.probability}%`} />
              <DetailRow icon="alert-circle-outline" label="Priority" value={deal.priority?.charAt(0).toUpperCase() + deal.priority?.slice(1)} />
              <DetailRow icon="globe-outline" label="Source" value={DEAL_SOURCES.find(s => s.id === deal.source)?.name || deal.source || 'Unknown'} />
              <DetailRow icon="calendar-outline" label="Due Date" value={deal.due_date ? new Date(deal.due_date).toLocaleDateString() : 'Not set'} />
              <DetailRow icon="calendar-outline" label="Created" value={deal.created_at ? new Date(deal.created_at).toLocaleDateString() : 'Unknown'} />
            </View>
          </View>

          {/* Stage Change */}
          <View style={styles.detailSection}>
            <Text style={styles.detailSectionTitle}>Move Stage</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {STAGES.map(s => (
                <TouchableOpacity key={s.id}
                  style={[styles.stageSelectChip, deal.stage === s.id && { backgroundColor: s.color + '25', borderColor: s.color }]}
                  onPress={() => onStageChange(deal.id, s.id)}
                >
                  <Ionicons name={s.icon} size={12} color={deal.stage === s.id ? s.color : BRAND.sub} />
                  <Text style={[styles.stageSelectText, deal.stage === s.id && { color: s.color }]}>{s.name}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          {/* Quick Actions */}
          <View style={styles.detailSection}>
            <Text style={styles.detailSectionTitle}>Actions</Text>
            <View style={styles.detailActionsRow}>
              {[
                { icon: 'mail-outline', label: 'Email', color: BRAND.mint, onPress: onEmailTemplate },
                { icon: 'calendar-outline', label: 'Activity', color: BRAND.blue, onPress: onAddActivity },
                { icon: 'checkmark-circle-outline', label: 'Mark Won', color: BRAND.success, onPress: () => onStageChange(deal.id, 'won') },
                { icon: 'close-circle-outline', label: 'Mark Lost', color: BRAND.danger, onPress: () => onStageChange(deal.id, 'lost') },
                { icon: 'trash-outline', label: 'Delete', color: BRAND.danger, onPress: () => onDelete(deal.id) },
              ].map((a, i) => (
                <TouchableOpacity key={i} style={styles.detailActionBtn} onPress={a.onPress}>
                  <View style={[styles.detailActionIcon, { backgroundColor: a.color + '18' }]}>
                    <Ionicons name={a.icon} size={18} color={a.color} />
                  </View>
                  <Text style={styles.detailActionLabel}>{a.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Tags */}
          {deal.tags?.length > 0 && (
            <View style={styles.detailSection}>
              <Text style={styles.detailSectionTitle}>Tags</Text>
              <View style={styles.tagsRow}>
                {deal.tags.map((tag, i) => <View key={i} style={styles.tag}><Text style={styles.tagText}>{tag}</Text></View>)}
              </View>
            </View>
          )}

          {/* Notes */}
          {deal.notes && (
            <View style={styles.detailSection}>
              <Text style={styles.detailSectionTitle}>Deal Notes</Text>
              <View style={styles.detailCard}><Text style={styles.dealNoteText}>{deal.notes}</Text></View>
            </View>
          )}

          {/* Activity Notes Timeline */}
          <View style={styles.detailSection}>
            <Text style={styles.detailSectionTitle}>Activity Log ({notes.length})</Text>
            <View style={styles.noteInputRow}>
              <TextInput
                style={styles.noteInput}
                placeholder="Add note..."
                value={newNote}
                onChangeText={setNewNote}
                placeholderTextColor="#94a3b8"
              />
              <TouchableOpacity style={styles.noteAddBtn} onPress={onAddNote}>
                <Ionicons name="send" size={16} color="#fff" />
              </TouchableOpacity>
            </View>
            {notes.map(note => (
              <View key={note.id} style={styles.noteItem}>
                <View style={styles.noteTimeline}>
                  <View style={styles.noteDot} />
                  <View style={styles.noteLineV} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.noteText}>{note.text}</Text>
                  <Text style={styles.noteMeta}>{note.created_by} · {new Date(note.created_at).toLocaleDateString()}</Text>
                </View>
              </View>
            ))}
          </View>
          <View style={{ height: 40 }} />
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
}

function DetailRow({ icon, label, value, valueColor }) {
  return (
    <View style={styles.detailRow}>
      <Ionicons name={icon} size={15} color={BRAND.sub} />
      <Text style={styles.detailRowLabel}>{label}</Text>
      <Text style={[styles.detailRowValue, valueColor && { color: valueColor }]} numberOfLines={1}>{value}</Text>
    </View>
  );
}

// ─── CONTACTS MODAL ────────────────────────────────────────────────────────
function ContactsModal({ visible, contacts, onClose, onSelect, onAdd }) {
  const [q, setQ] = useState('');
  const filtered = contacts.filter(c => !q || String(c.name ?? '').toLowerCase().includes(q.toLowerCase()) || c.company?.toLowerCase().includes(q.toLowerCase()));
  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <LinearGradient colors={[BRAND.canary, '#e0a500']} style={styles.modalHeaderGrad}>
            <TouchableOpacity onPress={onClose} style={styles.modalCloseBtn}><Ionicons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={styles.modalTitle}>Contacts ({contacts.length})</Text>
            <TouchableOpacity style={styles.modalSubmitBtn} onPress={onAdd}><Text style={styles.modalSubmitText}>+ Add</Text></TouchableOpacity>
          </LinearGradient>
        </View>
        <View style={[styles.searchContainer, { margin: 16 }]}>
          <Ionicons name="search" size={16} color={BRAND.sub} />
          <TextInput value={q} onChangeText={setQ} placeholder="Search contacts..." placeholderTextColor="#94a3b8" style={styles.searchInput} />
        </View>
        <ScrollView style={styles.modalBody}>
          {filtered.map(c => <ContactCard key={c.id} contact={c} onPress={() => onSelect(c)} />)}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
}

// ─── ADD CONTACT MODAL ─────────────────────────────────────────────────────
function AddContactModal({ visible, onClose, form, setForm, onSubmit }) {
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <LinearGradient colors={[BRAND.canary, '#e0a500']} style={styles.modalHeaderGrad}>
            <TouchableOpacity onPress={onClose} style={styles.modalCloseBtn}><Ionicons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={styles.modalTitle}>New Contact</Text>
            <TouchableOpacity style={styles.modalSubmitBtn} onPress={onSubmit}><Text style={styles.modalSubmitText}>Save</Text></TouchableOpacity>
          </LinearGradient>
        </View>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1 }}>
          <ScrollView style={styles.modalBody}>
            <TextInput style={styles.modalInput} placeholder="Full Name *" value={form.name} onChangeText={v => set('name', v)} placeholderTextColor="#94a3b8" />
            <TextInput style={styles.modalInput} placeholder="Company" value={form.company} onChangeText={v => set('company', v)} placeholderTextColor="#94a3b8" />
            <TextInput style={styles.modalInput} placeholder="Email" value={form.email} onChangeText={v => set('email', v)} placeholderTextColor="#94a3b8" keyboardType="email-address" autoCapitalize="none" />
            <TextInput style={styles.modalInput} placeholder="Phone" value={form.phone} onChangeText={v => set('phone', v)} placeholderTextColor="#94a3b8" keyboardType="phone-pad" />
            <Text style={styles.modalLabel}>Role</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 12 }}>
              {CONTACT_ROLES.map(r => (
                <TouchableOpacity key={r} style={[styles.sourceChip, form.role === r && styles.sourceChipActive]} onPress={() => set('role', r)}>
                  <Text style={[styles.sourceChipText, form.role === r && styles.sourceChipTextActive]}>{r}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <TextInput style={styles.modalInput} placeholder="LinkedIn URL (optional)" value={form.linkedin} onChangeText={v => set('linkedin', v)} placeholderTextColor="#94a3b8" autoCapitalize="none" />
            <TextInput style={[styles.modalInput, { height: 80, textAlignVertical: 'top' }]} placeholder="Notes..." value={form.notes} onChangeText={v => set('notes', v)} placeholderTextColor="#94a3b8" multiline />
            <View style={{ height: 40 }} />
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );
}

// ─── CONTACT DETAIL MODAL ──────────────────────────────────────────────────
function ContactDetailModal({ visible, contact, deals, onClose, onEmail }) {
  if (!contact) return null;
  const contactDeals = deals.filter(d => d.contact_email === contact.email);
  const scoreColor = contact.score >= 70 ? BRAND.success : contact.score >= 40 ? BRAND.warning : BRAND.danger;
  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <LinearGradient colors={[BRAND.canary, '#e0a500']} style={styles.modalHeaderGrad}>
            <TouchableOpacity onPress={onClose} style={styles.modalCloseBtn}><Ionicons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={styles.modalTitle}>{contact.name}</Text>
            <View style={[styles.scoreBadgeLg, { backgroundColor: scoreColor }]}>
              <Text style={styles.scoreBadgeLgText}>{contact.score}</Text>
            </View>
          </LinearGradient>
        </View>
        <ScrollView style={styles.modalBody}>
          <View style={styles.contactDetailHeader}>
            <View style={[styles.contactAvatarLg, { backgroundColor: BRAND.blue + '20' }]}>
              <Text style={styles.contactAvatarLgText}>{contact.name?.substring(0, 2).toUpperCase()}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.contactDetailName}>{contact.name}</Text>
              <Text style={styles.contactDetailRole}>{contact.role}</Text>
              <Text style={styles.contactDetailCompany}>{contact.company}</Text>
            </View>
          </View>
          <View style={styles.detailCard}>
            <DetailRow icon="mail-outline" label="Email" value={contact.email} />
            <DetailRow icon="call-outline" label="Phone" value={contact.phone || 'Not provided'} />
            <DetailRow icon="location-outline" label="Location" value={contact.location || 'Unknown'} />
            {contact.linkedin && <DetailRow icon="logo-linkedin" label="LinkedIn" value={contact.linkedin} />}
          </View>
          <View style={[styles.detailActionsRow, { marginTop: 16 }]}>
            <TouchableOpacity style={styles.detailActionBtn} onPress={onEmail}>
              <View style={[styles.detailActionIcon, { backgroundColor: BRAND.mint + '20' }]}>
                <Ionicons name="mail-outline" size={18} color={BRAND.mint} />
              </View>
              <Text style={styles.detailActionLabel}>Email</Text>
            </TouchableOpacity>
          </View>
          {contactDeals.length > 0 && (
            <View style={styles.detailSection}>
              <Text style={styles.detailSectionTitle}>Linked Deals ({contactDeals.length})</Text>
              {contactDeals.map(d => {
                const st = STAGES.find(s => s.id === d.stage);
                return (
                  <View key={d.id} style={[styles.detailCard, { marginBottom: 8 }]}>
                    <Text style={styles.contactDealName}>{d.company}</Text>
                    <Text style={[styles.contactDealStage, { color: st?.color }]}>{st?.name} · £{d.value?.toLocaleString()}</Text>
                  </View>
                );
              })}
            </View>
          )}
          {contact.notes && (
            <View style={styles.detailSection}>
              <Text style={styles.detailSectionTitle}>Notes</Text>
              <View style={styles.detailCard}><Text style={styles.dealNoteText}>{contact.notes}</Text></View>
            </View>
          )}
          <View style={{ height: 40 }} />
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
}

// ─── ANALYTICS MODAL ───────────────────────────────────────────────────────
function AnalyticsModal({ visible, deals, stats, users, forecastData, onClose }) {
  const won = deals.filter(d => d.stage === 'won');
  const lost = deals.filter(d => d.stage === 'lost');
  const active = deals.filter(d => !['won','lost'].includes(d.stage));
  const totalPipeline = active.reduce((s, d) => s + (d.value || 0), 0);
  const wonValue = won.reduce((s, d) => s + (d.value || 0), 0);

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <LinearGradient colors={[BRAND.mint, '#147a82']} style={styles.modalHeaderGrad}>
            <TouchableOpacity onPress={onClose} style={styles.modalCloseBtn}><Ionicons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={styles.modalTitle}>CRM Analytics</Text>
            <View style={{ width: 40 }} />
          </LinearGradient>
        </View>
        <ScrollView style={styles.modalBody}>
          {/* Key Metrics */}
          <View style={styles.analyticsMetricsRow}>
            {[
              { label: 'Pipeline Value', value: `£${(totalPipeline/1000).toFixed(0)}k`, color: BRAND.blue },
              { label: 'Won Revenue', value: `£${(wonValue/1000).toFixed(0)}k`, color: BRAND.success },
              { label: 'Win Rate', value: `${stats.winRate}%`, color: BRAND.canary },
              { label: 'Avg Cycle', value: `${stats.avgSalesCycle || 21}d`, color: BRAND.purple },
            ].map((m, i) => (
              <View key={i} style={[styles.analyticsMetric, { borderColor: m.color + '40' }]}>
                <Text style={[styles.analyticsMetricValue, { color: m.color }]}>{m.value}</Text>
                <Text style={styles.analyticsMetricLabel}>{m.label}</Text>
              </View>
            ))}
          </View>

          {/* Win / Loss */}
          <View style={styles.analyticsCard}>
            <Text style={styles.analyticsCardTitle}>Win / Loss Analysis</Text>
            <View style={styles.winLossRow}>
              <View style={[styles.winLossBar, { backgroundColor: BRAND.success + '20' }]}>
                <View style={[styles.winLossFill, { flex: won.length, backgroundColor: BRAND.success }]} />
                <View style={[styles.winLossFill, { flex: Math.max(lost.length, 1), backgroundColor: BRAND.danger }]} />
              </View>
            </View>
            <View style={styles.winLossLegend}>
              <View style={styles.legendItem}><View style={[styles.legendDot, { backgroundColor: BRAND.success }]} /><Text style={styles.legendText}>Won: {won.length}</Text></View>
              <View style={styles.legendItem}><View style={[styles.legendDot, { backgroundColor: BRAND.danger }]} /><Text style={styles.legendText}>Lost: {lost.length}</Text></View>
              <View style={styles.legendItem}><View style={[styles.legendDot, { backgroundColor: BRAND.blue }]} /><Text style={styles.legendText}>Active: {active.length}</Text></View>
            </View>
          </View>

          {/* Priority Distribution */}
          <View style={styles.analyticsCard}>
            <Text style={styles.analyticsCardTitle}>Priority Distribution</Text>
            {PRIORITIES.map(p => {
              const count = deals.filter(d => d.priority === p.id).length;
              const pct = deals.length > 0 ? Math.round((count / deals.length) * 100) : 0;
              return (
                <View key={p.id} style={styles.funnelRow}>
                  <Text style={styles.funnelLabel}>{p.name}</Text>
                  <View style={styles.funnelBarOuter}>
                    <View style={[styles.funnelBarInner, { width: `${pct}%`, backgroundColor: p.color }]} />
                  </View>
                  <Text style={[styles.funnelCount, { color: p.color }]}>{count}</Text>
                </View>
              );
            })}
          </View>

          {/* Team Leaderboard */}
          <View style={styles.analyticsCard}>
            <Text style={styles.analyticsCardTitle}>Sales Team Performance</Text>
            {users.sort((a,b) => (b.revenue||0)-(a.revenue||0)).map((user, i) => (
              <View key={user.id} style={styles.leaderRow}>
                <View style={[styles.leaderRank, { backgroundColor: i===0?BRAND.canary:i===1?'#94a3b8':'#cd7f32' }]}>
                  <Text style={styles.leaderRankText}>{i+1}</Text>
                </View>
                <View style={[styles.leaderAvatar, { backgroundColor: BRAND.blue + '20' }]}>
                  <Text style={[styles.leaderAvatarText, { color: BRAND.blue }]}>{user.avatar}</Text>
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.leaderName}>{user.name}</Text>
                  <Text style={styles.leaderSub}>{user.deals} deals · {user.winRate}% win rate</Text>
                </View>
                <Text style={[styles.leaderRevenue, { color: BRAND.success }]}>£{(user.revenue/1000).toFixed(0)}k</Text>
              </View>
            ))}
          </View>
          <View style={{ height: 40 }} />
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
}

// ─── FORECAST MODAL ────────────────────────────────────────────────────────
function ForecastModal({ visible, forecastData, stats, deals, onClose }) {
  const active = deals.filter(d => !['won','lost'].includes(d.stage));
  const projected = active.reduce((s, d) => s + ((d.value || 0) * (d.probability || 0) / 100), 0);
  const maxRev = Math.max(...forecastData.map(m => Math.max(m.revenue, m.target)), 1);

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <LinearGradient colors={[BRAND.success, '#059669']} style={styles.modalHeaderGrad}>
            <TouchableOpacity onPress={onClose} style={styles.modalCloseBtn}><Ionicons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={styles.modalTitle}>Revenue Forecast</Text>
            <View style={{ width: 40 }} />
          </LinearGradient>
        </View>
        <ScrollView style={styles.modalBody}>
          <View style={styles.analyticsMetricsRow}>
            {[
              { label: 'Projected (weighted)', value: `£${(projected/1000).toFixed(0)}k`, color: BRAND.blue },
              { label: 'This Month Target', value: `£${((forecastData[forecastData.length-1]?.target||0)/1000).toFixed(0)}k`, color: BRAND.canary },
              { label: 'Win Rate', value: `${stats.winRate}%`, color: BRAND.success },
              { label: 'Avg Deal Size', value: `£${(stats.averageDealSize/1000).toFixed(0)}k`, color: BRAND.purple },
            ].map((m,i) => (
              <View key={i} style={[styles.analyticsMetric, { borderColor: m.color + '40' }]}>
                <Text style={[styles.analyticsMetricValue, { color: m.color }]}>{m.value}</Text>
                <Text style={styles.analyticsMetricLabel}>{m.label}</Text>
              </View>
            ))}
          </View>

          <View style={styles.analyticsCard}>
            <Text style={styles.analyticsCardTitle}>6-Month Revenue vs Target</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View style={styles.barChartContainer}>
                {forecastData.map((m, i) => {
                  const revH = Math.max((m.revenue / maxRev) * 160, 4);
                  const tarH = Math.max((m.target / maxRev) * 160, 4);
                  const beat = m.revenue >= m.target;
                  return (
                    <View key={i} style={styles.barGroup}>
                      <View style={styles.barsRow}>
                        <View style={[styles.bar, { height: tarH, backgroundColor: BRAND.line + 'cc', marginRight: 3 }]} />
                        <View style={[styles.bar, { height: revH, backgroundColor: beat ? BRAND.success : BRAND.blue }]} />
                      </View>
                      <Text style={styles.barLabel}>{m.month}</Text>
                      <Text style={[styles.barValueLabel, { color: beat ? BRAND.success : BRAND.blue }]}>
                        £{(m.revenue/1000).toFixed(0)}k
                      </Text>
                    </View>
                  );
                })}
              </View>
            </ScrollView>
            <View style={styles.barLegend}>
              <View style={styles.legendItem}><View style={[styles.legendDot, { backgroundColor: BRAND.blue }]} /><Text style={styles.legendText}>Actual Revenue</Text></View>
              <View style={styles.legendItem}><View style={[styles.legendDot, { backgroundColor: BRAND.line }]} /><Text style={styles.legendText}>Target</Text></View>
              <View style={styles.legendItem}><View style={[styles.legendDot, { backgroundColor: BRAND.success }]} /><Text style={styles.legendText}>Beat Target</Text></View>
            </View>
          </View>

          <View style={styles.analyticsCard}>
            <Text style={styles.analyticsCardTitle}>Weighted Pipeline Deals</Text>
            {active.sort((a,b) => (b.value*b.probability/100)-(a.value*a.probability/100)).slice(0,8).map(deal => {
              const weighted = Math.round((deal.value || 0) * (deal.probability || 0) / 100);
              return (
                <View key={deal.id} style={styles.forecastDealRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.forecastDealName} numberOfLines={1}>{deal.company}</Text>
                    <Text style={styles.forecastDealStage}>{STAGES.find(s=>s.id===deal.stage)?.name} · {deal.probability}%</Text>
                  </View>
                  <View style={{ alignItems: 'flex-end' }}>
                    <Text style={styles.forecastDealFull}>£{deal.value?.toLocaleString()}</Text>
                    <Text style={[styles.forecastDealWeighted, { color: BRAND.success }]}>£{weighted.toLocaleString()}</Text>
                  </View>
                </View>
              );
            })}
          </View>
          <View style={{ height: 40 }} />
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
}

// ─── AI ADVISOR MODAL ──────────────────────────────────────────────────────
function AIAdvisorModal({ visible, onClose, aiHistory, aiLoading, aiQuery, setAiQuery, onAsk, deals, stats }) {
  const scrollRef = useRef(null);
  useEffect(() => { if (visible) setTimeout(() => scrollRef.current?.scrollToEnd?.({ animated: true }), 100); }, [aiHistory, visible]);
  const prompts = [
    'How can I improve my win rate?', 'Which deals should I prioritise?',
    'Generate a closing strategy for my top deal', 'Analyse my pipeline health',
    'What\'s my biggest revenue risk?', 'Tips for negotiation stage deals',
    'How to reduce my sales cycle?', 'Best outreach tactics for cold leads',
  ];
  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <LinearGradient colors={['#7c3aed', '#5b21b6']} style={styles.modalHeaderGrad}>
            <TouchableOpacity onPress={onClose} style={styles.modalCloseBtn}><Ionicons name="close" size={22} color="#fff" /></TouchableOpacity>
            <View style={{ flex: 1 }}>
              <Text style={styles.modalTitle}>AI Sales Advisor</Text>
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>Powered by Claude AI</Text>
            </View>
            <View style={[styles.headerBtn, { backgroundColor: 'rgba(255,255,255,0.15)' }]}>
              <Ionicons name="sparkles" size={18} color="#fff" />
            </View>
          </LinearGradient>
        </View>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1 }}>
          {aiHistory.length === 0 ? (
            <ScrollView style={styles.modalBody} contentContainerStyle={{ paddingBottom: 20 }}>
              <View style={styles.aiWelcome}>
                <View style={styles.aiAvatarBig}>
                  <Ionicons name="sparkles" size={32} color="#7c3aed" />
                </View>
                <Text style={styles.aiWelcomeTitle}>Your AI Sales Advisor</Text>
                <Text style={styles.aiWelcomeSub}>
                  I have access to your full pipeline ({deals.length} deals, £{(stats.totalValue/1000).toFixed(0)}k value, {stats.winRate}% win rate). Ask me anything about sales strategy, deal review, or forecasting.
                </Text>
              </View>
              <Text style={styles.aiPromptsTitle}>Suggested Questions</Text>
              <View style={styles.aiPromptsGrid}>
                {prompts.map((p, i) => (
                  <TouchableOpacity key={i} style={styles.aiPromptBtn} onPress={() => onAsk(p)}>
                    <Text style={styles.aiPromptText}>{p}</Text>
                    <Ionicons name="arrow-forward" size={12} color="#7c3aed" />
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          ) : (
            <ScrollView ref={scrollRef} style={styles.modalBody} contentContainerStyle={{ paddingBottom: 16 }}>
              {aiHistory.map((msg, i) => (
                <View key={i} style={[styles.chatBubbleRow, msg.role === 'user' && { justifyContent: 'flex-end' }]}>
                  {msg.role === 'assistant' && (
                    <View style={styles.chatAvatar}>
                      <Ionicons name="sparkles" size={14} color="#7c3aed" />
                    </View>
                  )}
                  <View style={[styles.chatBubble, msg.role === 'user' ? styles.chatBubbleUser : styles.chatBubbleAI]}>
                    <Text style={[styles.chatBubbleText, msg.role === 'user' && { color: '#fff' }]}>{msg.text}</Text>
                    <Text style={[styles.chatTime, msg.role === 'user' && { color: 'rgba(255,255,255,0.6)' }]}>
                      {new Date(msg.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Text>
                  </View>
                </View>
              ))}
              {aiLoading && (
                <View style={styles.chatBubbleRow}>
                  <View style={styles.chatAvatar}><Ionicons name="sparkles" size={14} color="#7c3aed" /></View>
                  <View style={[styles.chatBubble, styles.chatBubbleAI, { paddingVertical: 12 }]}>
                    <ActivityIndicator size="small" color="#7c3aed" />
                  </View>
                </View>
              )}
            </ScrollView>
          )}
          <View style={styles.chatInputRow}>
            <TextInput
              style={styles.chatInput}
              placeholder="Ask about your pipeline..."
              value={aiQuery}
              onChangeText={setAiQuery}
              placeholderTextColor="#94a3b8"
              multiline
              maxLength={500}
              onSubmitEditing={() => onAsk(aiQuery)}
            />
            <TouchableOpacity
              style={[styles.chatSendBtn, { backgroundColor: aiQuery.trim() ? '#7c3aed' : BRAND.line }]}
              onPress={() => onAsk(aiQuery)}
              disabled={!aiQuery.trim() || aiLoading}
            >
              <Ionicons name="send" size={18} color={aiQuery.trim() ? '#fff' : BRAND.sub} />
            </TouchableOpacity>
          </View>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );
}

// ─── EMAIL TEMPLATES MODAL ────────────────────────────────────────────────
function EmailTemplatesModal({ visible, templates, deal, onClose }) {
  const [selected, setSelected] = useState(null);
  const fill = (text) => {
    if (!text || !deal) return text || '';
    return text
      .replace(/\{\{company\}\}/g, deal.company || 'your company')
      .replace(/\{\{contact_name\}\}/g, deal.contact_name || 'there')
      .replace(/\{\{deal_title\}\}/g, deal.company + ' deal' || 'your deal')
      .replace(/\{\{deal_value\}\}/g, deal.value?.toLocaleString() || '0')
      .replace(/\{\{rep_name\}\}/g, deal.assigned_user?.name || 'Your Name')
      .replace(/\{\{due_date\}\}/g, deal.due_date ? new Date(deal.due_date).toLocaleDateString() : 'soon');
  };
  const handleShare = async (template) => {
    await Share.share({ message: `Subject: ${fill(template.subject)}\n\n${fill(template.body)}`, title: template.name });
  };
  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <LinearGradient colors={[BRAND.warning, '#d97706']} style={styles.modalHeaderGrad}>
            <TouchableOpacity onPress={onClose} style={styles.modalCloseBtn}><Ionicons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={styles.modalTitle}>Email Templates</Text>
            <View style={{ width: 40 }} />
          </LinearGradient>
        </View>
        <ScrollView style={styles.modalBody}>
          {deal && (
            <View style={[styles.detailCard, { marginBottom: 16, borderColor: BRAND.warning + '40' }]}>
              <Text style={styles.modalLabel}>Personalising for: {deal.company}</Text>
              <Text style={styles.activityMeta}>Contact: {deal.contact_name}</Text>
            </View>
          )}
          {!selected ? (
            templates.map(t => (
              <TouchableOpacity key={t.id} style={styles.templateCard} onPress={() => setSelected(t)} activeOpacity={0.75}>
                <View style={[styles.templateIcon, { backgroundColor: BRAND.warning + '20' }]}>
                  <Ionicons name={t.icon} size={20} color={BRAND.warning} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.templateName}>{t.name}</Text>
                  <Text style={styles.templatePreview} numberOfLines={2}>{fill(t.subject)}</Text>
                </View>
                <Ionicons name="chevron-forward" size={18} color={BRAND.sub} />
              </TouchableOpacity>
            ))
          ) : (
            <View>
              <TouchableOpacity style={styles.backBtn} onPress={() => setSelected(null)}>
                <Ionicons name="arrow-back" size={16} color={BRAND.blue} />
                <Text style={styles.backBtnText}>Back to templates</Text>
              </TouchableOpacity>
              <Text style={styles.analyticsCardTitle}>{selected.name}</Text>
              <View style={[styles.detailCard, { marginBottom: 8 }]}>
                <Text style={styles.modalLabel}>Subject</Text>
                <Text style={styles.emailSubject}>{fill(selected.subject)}</Text>
              </View>
              <View style={styles.detailCard}>
                <Text style={styles.modalLabel}>Body</Text>
                <Text style={styles.emailBody}>{fill(selected.body)}</Text>
              </View>
              <TouchableOpacity style={[styles.submitBtn, { backgroundColor: BRAND.warning, marginTop: 16 }]} onPress={() => handleShare(selected)}>
                <Ionicons name="share-outline" size={18} color="#fff" />
                <Text style={styles.submitBtnText}>Share / Copy Email</Text>
              </TouchableOpacity>
            </View>
          )}
          <View style={{ height: 40 }} />
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
}

// ─── TEAM MODAL ────────────────────────────────────────────────────────────
function TeamModal({ visible, users, deals, onClose }) {
  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <LinearGradient colors={[BRAND.info, '#1d4ed8']} style={styles.modalHeaderGrad}>
            <TouchableOpacity onPress={onClose} style={styles.modalCloseBtn}><Ionicons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={styles.modalTitle}>Sales Team</Text>
            <View style={{ width: 40 }} />
          </LinearGradient>
        </View>
        <ScrollView style={styles.modalBody}>
          {users.sort((a,b) => (b.revenue||0)-(a.revenue||0)).map((user, i) => {
            const userDeals = deals.filter(d => d.assigned_user?.id === user.id || d.assigned_user?.name === user.name);
            return (
              <View key={user.id} style={styles.teamCard}>
                <View style={styles.teamCardHeader}>
                  <View style={[styles.teamRankBadge, { backgroundColor: i===0?BRAND.canary:i===1?'#94a3b8':'#cd7f32' }]}>
                    <Text style={styles.teamRankText}>#{i+1}</Text>
                  </View>
                  <View style={[styles.leaderAvatar, { backgroundColor: BRAND.blue + '20', width: 42, height: 42, borderRadius: 21 }]}>
                    <Text style={[styles.leaderAvatarText, { color: BRAND.blue, fontSize: 15 }]}>{user.avatar}</Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.teamCardName}>{user.name}</Text>
                    <Text style={styles.teamCardRole}>{user.role === 'sales' ? 'Sales Executive' : user.role}</Text>
                  </View>
                  <View style={{ alignItems: 'flex-end' }}>
                    <Text style={[styles.leaderRevenue, { color: BRAND.success }]}>£{(user.revenue/1000).toFixed(0)}k</Text>
                    <Text style={styles.teamCardWinRate}>{user.winRate}% win</Text>
                  </View>
                </View>
                <View style={styles.teamStatsRow}>
                  {[
                    { label: 'Total Deals', value: userDeals.length || user.deals },
                    { label: 'Active', value: userDeals.filter(d=>!['won','lost'].includes(d.stage)).length || Math.ceil((user.deals||0)*0.6) },
                    { label: 'Won', value: userDeals.filter(d=>d.stage==='won').length || Math.ceil((user.deals||0)*0.4) },
                    { label: 'Win Rate', value: `${user.winRate}%` },
                  ].map((s, si) => (
                    <View key={si} style={styles.teamStat}>
                      <Text style={styles.teamStatValue}>{s.value}</Text>
                      <Text style={styles.teamStatLabel}>{s.label}</Text>
                    </View>
                  ))}
                </View>
              </View>
            );
          })}
          <View style={{ height: 40 }} />
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
}

// ─── PIPELINE KANBAN MODAL ─────────────────────────────────────────────────
function PipelineKanbanModal({ visible, deals, onClose, onDealPress, onStageChange }) {
  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <LinearGradient colors={[BRAND.blue, '#163b6c']} style={styles.modalHeaderGrad}>
            <TouchableOpacity onPress={onClose} style={styles.modalCloseBtn}><Ionicons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={styles.modalTitle}>Pipeline Kanban</Text>
            <View style={{ width: 40 }} />
          </LinearGradient>
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator style={{ flex: 1, paddingTop: 12 }}>
          {STAGES.map(stage => {
            const stageDeals = deals.filter(d => d.stage === stage.id);
            const stageValue = stageDeals.reduce((s, d) => s + (d.value || 0), 0);
            return (
              <View key={stage.id} style={[styles.kanbanCol]}>
                <View style={[styles.kanbanColHeader, { backgroundColor: stage.color }]}>
                  <Ionicons name={stage.icon} size={13} color="#fff" />
                  <Text style={styles.kanbanColTitle}>{stage.name}</Text>
                  <View style={styles.kanbanCount}><Text style={styles.kanbanCountText}>{stageDeals.length}</Text></View>
                </View>
                <Text style={[styles.kanbanValue, { color: stage.color }]}>£{(stageValue/1000).toFixed(0)}k</Text>
                <ScrollView showsVerticalScrollIndicator={false}>
                  {stageDeals.map(deal => (
                    <TouchableOpacity key={deal.id} style={styles.kanbanCard} onPress={() => onDealPress(deal)} activeOpacity={0.75}>
                      <Text style={styles.kanbanCardCompany} numberOfLines={1}>{deal.company}</Text>
                      <Text style={styles.kanbanCardContact} numberOfLines={1}>{deal.contact_name}</Text>
                      <Text style={[styles.kanbanCardValue, { color: stage.color }]}>£{deal.value?.toLocaleString()}</Text>
                      <View style={styles.probBarOuter}>
                        <View style={[styles.probBarInner, { width: `${deal.probability}%`, backgroundColor: stage.color }]} />
                      </View>
                      <View style={styles.kanbanCardFooter}>
                        <Text style={styles.kanbanProbText}>{deal.probability}%</Text>
                        <View style={[styles.scoreBadge, { backgroundColor: (deal.score >= 70 ? BRAND.success : BRAND.warning) + '25' }]}>
                          <Text style={[styles.scoreText, { color: deal.score >= 70 ? BRAND.success : BRAND.warning }]}>{deal.score}</Text>
                        </View>
                      </View>
                    </TouchableOpacity>
                  ))}
                  {stageDeals.length === 0 && <View style={styles.kanbanEmpty}><Text style={styles.kanbanEmptyText}>Empty</Text></View>}
                </ScrollView>
              </View>
            );
          })}
          <View style={{ width: 16 }} />
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
}

// ─── FILTERS MODAL ────────────────────────────────────────────────────────
function FiltersModal({ visible, onClose, filterPriority, setFilterPriority, sortBy, setSortBy }) {
  return (
    <Modal visible={visible} animationType="slide" presentationStyle="formSheet" onRequestClose={onClose}>
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <LinearGradient colors={[BRAND.sub, '#334155']} style={styles.modalHeaderGrad}>
            <TouchableOpacity onPress={onClose} style={styles.modalCloseBtn}><Ionicons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={styles.modalTitle}>Filters & Sort</Text>
            <TouchableOpacity style={styles.modalSubmitBtn} onPress={() => { setFilterPriority('all'); setSortBy('updated_at'); onClose(); }}>
              <Text style={styles.modalSubmitText}>Reset</Text>
            </TouchableOpacity>
          </LinearGradient>
        </View>
        <ScrollView style={styles.modalBody}>
          <Text style={styles.modalSection}>Filter by Priority</Text>
          <View style={styles.priorityRow}>
            {[{ id: 'all', name: 'All', color: BRAND.text }, ...PRIORITIES].map(p => (
              <TouchableOpacity key={p.id} style={[styles.priorityChip, filterPriority === p.id && { backgroundColor: p.color + '25', borderColor: p.color }]} onPress={() => setFilterPriority(p.id)}>
                <Text style={[styles.priorityChipText, filterPriority === p.id && { color: p.color }]}>{p.name}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <Text style={styles.modalSection}>Sort By</Text>
          {[
            { id: 'updated_at', label: 'Most Recent', icon: 'time-outline' },
            { id: 'value', label: 'Deal Value (High → Low)', icon: 'cash' },
            { id: 'score', label: 'Lead Score (High → Low)', icon: 'star-outline' },
            { id: 'probability', label: 'Probability (High → Low)', icon: 'analytics-outline' },
          ].map(s => (
            <TouchableOpacity key={s.id} style={[styles.sortOptionRow, sortBy === s.id && { backgroundColor: BRAND.blue + '10' }]} onPress={() => setSortBy(s.id)}>
              <Ionicons name={s.icon} size={18} color={sortBy === s.id ? BRAND.blue : BRAND.sub} />
              <Text style={[styles.sortOptionText, sortBy === s.id && { color: BRAND.blue, fontWeight: '700' }]}>{s.label}</Text>
              {sortBy === s.id && <Ionicons name="checkmark" size={18} color={BRAND.blue} />}
            </TouchableOpacity>
          ))}
          <TouchableOpacity style={[styles.submitBtn, { backgroundColor: BRAND.blue, marginTop: 20 }]} onPress={onClose}>
            <Text style={styles.submitBtnText}>Apply Filters</Text>
          </TouchableOpacity>
          <View style={{ height: 40 }} />
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
}

// ─── STYLES ────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: BRAND.bg },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: BRAND.bg },
  loadingText: { marginTop: 12, color: BRAND.sub, fontSize: 14 },

  // Header
  header: { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1000, elevation: 4 },
  headerGradient: { paddingTop: 62, paddingHorizontal: 16, paddingBottom: 1, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
  headerRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  headerBtn: { width: 36, height: 36, borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.15)', alignItems: 'center', justifyContent: 'center' },
  headerActions: { flexDirection: 'row', gap: 6 },
  headerTitle: { color: '#fff', fontSize: 20, fontWeight: '900' },
  headerSubtitle: { color: 'rgba(255,255,255,0.8)', fontSize: 11, marginTop: 2 },
  tab: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 10, marginRight: 6, backgroundColor: 'rgba(255,255,255,0.12)' },
  tabActive: { backgroundColor: '#fff' },
  tabText: { color: 'rgba(255,255,255,0.8)', fontSize: 12, fontWeight: '600' },

  // Error
  errorBanner: { backgroundColor: BRAND.danger, flexDirection: 'row', alignItems: 'center', padding: 10, marginTop: 4, marginHorizontal: 16, borderRadius: 10, gap: 8 },
  errorText: { flex: 1, color: '#fff', fontSize: 13 },
  retryText: { color: '#fff', fontWeight: 'bold', textDecorationLine: 'underline' },

  // KPI
  kpiCard: { width: 110, backgroundColor: BRAND.white, borderRadius: 16, padding: 14, borderWidth: 1, borderColor: BRAND.line, marginRight: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 6, elevation: 2 },
  kpiIconBg: { width: 32, height: 32, borderRadius: 10, alignItems: 'center', justifyContent: 'center', marginBottom: 8 },
  kpiValue: { fontSize: 18, fontWeight: '900', color: BRAND.text, marginBottom: 2 },
  kpiLabel: { fontSize: 10, color: BRAND.sub, fontWeight: '600' },
  kpiTrendRow: { flexDirection: 'row', alignItems: 'center', gap: 3, marginTop: 4 },
  kpiTrend: { fontSize: 10, color: BRAND.success, fontWeight: '700' },

  // Stats Row
  statsRow: { flexDirection: 'row', marginHorizontal: 16, gap: 10, marginBottom: 16 },
  statPill: { flex: 1, backgroundColor: BRAND.white, borderRadius: 12, padding: 12, alignItems: 'center', gap: 4, borderWidth: 1, borderColor: BRAND.line },
  statPillValue: { fontSize: 15, fontWeight: '800' },
  statPillLabel: { fontSize: 10, color: BRAND.sub, fontWeight: '600' },

  // Search
  searchContainer: { flexDirection: 'row', alignItems: 'center', backgroundColor: BRAND.white, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 10, marginHorizontal: 16, marginBottom: 12, borderWidth: 1, borderColor: BRAND.line, gap: 10 },
  searchInput: { flex: 1, fontSize: 14, color: BRAND.text, padding: 0 },

  // Stage Chips
  stageScroll: { paddingLeft: 16, marginBottom: 16 },
  stageChip: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 20, backgroundColor: BRAND.white, borderWidth: 1, borderColor: BRAND.line, marginRight: 8 },
  stageDot: { width: 7, height: 7, borderRadius: 4 },
  stageChipText: { fontSize: 12, fontWeight: '600', color: BRAND.sub },

  // Section
  section: { marginHorizontal: 16, marginBottom: 20 },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  sectionTitle: { fontSize: 16, fontWeight: '800', color: BRAND.text },
  smallBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8, backgroundColor: BRAND.blue + '12' },
  smallBtnText: { fontSize: 12, color: BRAND.blue, fontWeight: '700' },
  sortBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 5, borderRadius: 8, backgroundColor: BRAND.blue + '12' },
  sortBtnText: { fontSize: 11, color: BRAND.blue, fontWeight: '700' },

  // Quick Actions Grid
  actionsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  actionBtn: { width: (width - 72) / 4, alignItems: 'center', gap: 6 },
  actionBtnIcon: { width: 50, height: 50, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  actionBtnLabel: { fontSize: 10, color: BRAND.text, fontWeight: '600', textAlign: 'center', lineHeight: 13 },

  // Deal Card
  dealCard: { backgroundColor: BRAND.white, borderRadius: 16, marginBottom: 12, borderWidth: 1, borderColor: BRAND.line, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 8, elevation: 2, overflow: 'hidden', flexDirection: 'row' },
  dealScoreBar: { width: 4, borderRadius: 0 },
  dealCardInner: { flex: 1, padding: 14 },
  dealHeader: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 10 },
  dealCompany: { fontSize: 15, fontWeight: '800', color: BRAND.text, marginBottom: 3 },
  dealContact: { fontSize: 12, color: BRAND.sub, marginBottom: 2 },
  assignedTo: { fontSize: 11, color: BRAND.sub },
  dealValue: { fontSize: 16, fontWeight: '900', color: BRAND.success, marginBottom: 3 },
  scoreBadge: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6 },
  scoreText: { fontSize: 10, fontWeight: '800' },
  dealBadges: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 10 },
  stageBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, borderWidth: 1 },
  stageText: { fontSize: 11, fontWeight: '700' },
  priorityBadge: { paddingHorizontal: 7, paddingVertical: 3, borderRadius: 6 },
  priorityText: { fontSize: 11, fontWeight: '700' },
  sourceBadge: { paddingHorizontal: 7, paddingVertical: 3, borderRadius: 6, backgroundColor: BRAND.line },
  sourceText: { fontSize: 10, color: BRAND.sub, fontWeight: '600' },
  probRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  probBarOuter: { flex: 1, height: 5, backgroundColor: BRAND.line, borderRadius: 3, overflow: 'hidden' },
  probBarInner: { height: 5, borderRadius: 3 },
  probText: { fontSize: 11, color: BRAND.sub, fontWeight: '600', minWidth: 35 },
  nextAction: { flexDirection: 'row', alignItems: 'center', gap: 5, marginBottom: 8 },
  nextActionText: { fontSize: 12, color: BRAND.blue, flex: 1 },
  tagsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 6 },
  tag: { paddingHorizontal: 7, paddingVertical: 2, borderRadius: 5, backgroundColor: BRAND.blue + '15' },
  tagText: { fontSize: 10, color: BRAND.blue, fontWeight: '600' },
  moreTags: { fontSize: 10, color: BRAND.sub, alignSelf: 'center' },
  dealFooterRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 },
  dueDateBadge: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  dueDateText: { fontSize: 11, color: BRAND.sub },
  dealActions: { flexDirection: 'row', gap: 6 },
  actionIcon: { width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: BRAND.line + '50' },

  // Activity Item
  activityItem: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, backgroundColor: BRAND.white, borderRadius: 12, padding: 12, marginBottom: 8, borderWidth: 1, borderColor: BRAND.line },
  activityDone: { opacity: 0.6 },
  activityIconBg: { width: 36, height: 36, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  activityTitle: { fontSize: 13, fontWeight: '700', color: BRAND.text, marginBottom: 2 },
  activityDesc: { fontSize: 11, color: BRAND.sub, marginBottom: 2 },
  activityMeta: { fontSize: 10, color: BRAND.sub },
  activityRight: { alignItems: 'flex-end', gap: 4 },
  activityTime: { fontSize: 11, color: BRAND.sub, fontWeight: '600' },
  actTypeBadge: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 5 },
  actTypeBadgeText: { fontSize: 9, fontWeight: '700' },

  // Contact Card
  contactCard: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: BRAND.white, borderRadius: 14, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: BRAND.line },
  contactAvatar: { width: 42, height: 42, borderRadius: 21, alignItems: 'center', justifyContent: 'center' },
  contactAvatarText: { fontSize: 15, fontWeight: '800', color: BRAND.blue },
  contactName: { fontSize: 14, fontWeight: '800', color: BRAND.text },
  contactRole: { fontSize: 11, color: BRAND.sub, marginTop: 2 },
  contactEmail: { fontSize: 11, color: BRAND.blue, marginTop: 1 },
  dealsBadge: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6, backgroundColor: BRAND.mint + '20' },
  dealsBadgeText: { fontSize: 10, color: BRAND.mint, fontWeight: '700' },

  // Pipeline
  pipelineCol: { width: 180, marginRight: 12 },
  pipelineColHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, paddingVertical: 8, borderRadius: 10, marginBottom: 4 },
  pipelineColTitle: { flex: 1, color: '#fff', fontSize: 12, fontWeight: '800' },
  pipelineCount: { backgroundColor: 'rgba(255,255,255,0.25)', borderRadius: 5, paddingHorizontal: 5, paddingVertical: 1 },
  pipelineCountText: { color: '#fff', fontSize: 10, fontWeight: '800' },
  pipelineValue: { fontSize: 12, fontWeight: '700', marginBottom: 8, paddingHorizontal: 2 },
  pipelineCard: { backgroundColor: BRAND.white, borderRadius: 12, padding: 12, marginBottom: 10, borderWidth: 1, borderColor: BRAND.line },
  pipelineCardCompany: { fontSize: 13, fontWeight: '800', color: BRAND.text, marginBottom: 2 },
  pipelineCardContact: { fontSize: 11, color: BRAND.sub, marginBottom: 6 },
  pipelineCardFooter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
  pipelineCardValue: { fontSize: 13, fontWeight: '800' },
  pipelineScore: { paddingHorizontal: 5, paddingVertical: 2, borderRadius: 5 },
  pipelineScoreText: { fontSize: 11, fontWeight: '800' },
  pipelineProbText: { fontSize: 10, color: BRAND.sub, fontWeight: '600', marginTop: 2 },
  pipelineEmpty: { backgroundColor: BRAND.line + '40', borderRadius: 10, padding: 20, alignItems: 'center' },
  pipelineEmptyText: { fontSize: 12, color: BRAND.sub },

  // Activity Tabs
  actTypeChip: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16, borderWidth: 1, marginRight: 8 },
  actTypeText: { fontSize: 11, fontWeight: '700' },
  actTypeHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 },
  actTypeHeaderText: { fontSize: 13, fontWeight: '800' },
  actTypesGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  actTypeBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 10, borderWidth: 1, borderColor: BRAND.line, backgroundColor: BRAND.white },
  actTypeBtnText: { fontSize: 12, color: BRAND.sub, fontWeight: '600' },

  // Analytics
  analyticsCard: { backgroundColor: BRAND.white, borderRadius: 16, padding: 16, marginBottom: 16, borderWidth: 1, borderColor: BRAND.line },
  analyticsCardTitle: { fontSize: 14, fontWeight: '800', color: BRAND.text, marginBottom: 14 },
  analyticsMetricsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 },
  analyticsMetric: { width: (width - 64) / 2, backgroundColor: BRAND.white, borderRadius: 14, padding: 14, borderWidth: 1 },
  analyticsMetricValue: { fontSize: 22, fontWeight: '900', marginBottom: 4 },
  analyticsMetricLabel: { fontSize: 11, color: BRAND.sub, fontWeight: '600' },
  funnelRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 },
  funnelLabel: { width: 80, fontSize: 11, color: BRAND.sub, fontWeight: '600' },
  funnelBarOuter: { flex: 1, height: 8, backgroundColor: BRAND.line, borderRadius: 4, overflow: 'hidden' },
  funnelBarInner: { height: 8, borderRadius: 4 },
  funnelCount: { width: 36, fontSize: 11, fontWeight: '800', textAlign: 'right', color: BRAND.text },
  leaderRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 },
  leaderRank: { width: 22, height: 22, borderRadius: 6, alignItems: 'center', justifyContent: 'center' },
  leaderRankText: { color: '#fff', fontSize: 10, fontWeight: '900' },
  leaderAvatar: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
  leaderAvatarText: { fontSize: 12, fontWeight: '800' },
  leaderName: { fontSize: 13, fontWeight: '800', color: BRAND.text },
  leaderSub: { fontSize: 11, color: BRAND.sub },
  leaderRevenue: { fontSize: 14, fontWeight: '900' },
  winLossRow: { marginBottom: 10 },
  winLossBar: { height: 12, borderRadius: 6, overflow: 'hidden', flexDirection: 'row' },
  winLossFill: { height: 12 },
  winLossLegend: { flexDirection: 'row', gap: 16 },
  barChartContainer: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, paddingHorizontal: 4, paddingBottom: 4, minHeight: 180 },
  barGroup: { alignItems: 'center', width: 40 },
  barsRow: { flexDirection: 'row', alignItems: 'flex-end', marginBottom: 4 },
  bar: { width: 14, borderRadius: 4 },
  barLabel: { fontSize: 10, color: BRAND.sub, fontWeight: '600' },
  barValueLabel: { fontSize: 9, fontWeight: '800', marginTop: 2 },
  barLegend: { flexDirection: 'row', gap: 16, marginTop: 10 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  legendDot: { width: 8, height: 8, borderRadius: 4 },
  legendText: { fontSize: 11, color: BRAND.sub },

  // Forecast
  forecastDealRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  forecastDealName: { fontSize: 13, fontWeight: '700', color: BRAND.text, marginBottom: 2 },
  forecastDealStage: { fontSize: 11, color: BRAND.sub },
  forecastDealFull: { fontSize: 11, color: BRAND.sub, fontWeight: '600' },
  forecastDealWeighted: { fontSize: 13, fontWeight: '800' },

  // Empty
  emptyState: { alignItems: 'center', padding: 30 },
  emptyTitle: { fontSize: 15, fontWeight: '800', color: BRAND.text, marginTop: 12, marginBottom: 6 },
  emptyText: { fontSize: 13, color: BRAND.sub, textAlign: 'center' },
  emptyButton: { marginTop: 14, backgroundColor: BRAND.blue, borderRadius: 10, paddingHorizontal: 20, paddingVertical: 10 },
  emptyButtonText: { color: '#fff', fontWeight: '800', fontSize: 13 },

  // Integration
  integrationRow: { flexDirection: 'row', gap: 12 },
  integrationCard: { flex: 1, borderRadius: 14, borderWidth: 1, overflow: 'hidden' },
  integrationGrad: { padding: 14, gap: 6 },
  integrationTitle: { fontSize: 13, fontWeight: '800' },
  integrationSub: { fontSize: 11, color: BRAND.sub },

  // FAB
  fab: { position: 'absolute', bottom: 28, right: 20, zIndex: 100 },
  fabInner: { width: 54, height: 54, borderRadius: 16, alignItems: 'center', justifyContent: 'center', shadowColor: BRAND.blue, shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.4, shadowRadius: 10, elevation: 8 },

  // Modal
  modalContainer: { flex: 1, backgroundColor: BRAND.bg },
  modalHeader: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 6, elevation: 4 },
  modalHeaderGrad: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 14, gap: 12 },
  modalCloseBtn: { width: 34, height: 34, borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },
  modalTitle: { flex: 1, color: '#fff', fontSize: 17, fontWeight: '900' },
  modalSubmitBtn: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.2)' },
  modalSubmitText: { color: '#fff', fontWeight: '800', fontSize: 14 },
  modalBody: { flex: 1, paddingHorizontal: 16, paddingTop: 16 },
  modalSection: { fontSize: 13, fontWeight: '800', color: BRAND.sub, marginBottom: 10, marginTop: 6, textTransform: 'uppercase', letterSpacing: 0.5 },
  modalLabel: { fontSize: 13, fontWeight: '700', color: BRAND.text, marginBottom: 8 },
  modalInput: { backgroundColor: BRAND.white, borderRadius: 12, borderWidth: 1, borderColor: BRAND.line, paddingHorizontal: 14, paddingVertical: 12, fontSize: 14, color: BRAND.text, marginBottom: 12 },
  modalRow: { marginBottom: 12 },

  // Form Selectors
  probChip: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8, backgroundColor: BRAND.line + '60', marginRight: 6 },
  probChipActive: { backgroundColor: BRAND.blue },
  probChipText: { fontSize: 12, color: BRAND.sub, fontWeight: '600' },
  probChipTextActive: { color: '#fff', fontWeight: '800' },
  stageSelectChip: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 10, backgroundColor: BRAND.white, borderWidth: 1, borderColor: BRAND.line, marginRight: 8 },
  stageSelectText: { fontSize: 12, fontWeight: '700', color: BRAND.sub },
  priorityRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 14 },
  priorityChip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 10, backgroundColor: BRAND.white, borderWidth: 1, borderColor: BRAND.line },
  priorityChipText: { fontSize: 12, fontWeight: '700', color: BRAND.sub },
  sourceChip: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 10, backgroundColor: BRAND.white, borderWidth: 1, borderColor: BRAND.line, marginRight: 8 },
  sourceChipActive: { backgroundColor: BRAND.blue + '20', borderColor: BRAND.blue },
  sourceChipText: { fontSize: 12, color: BRAND.sub, fontWeight: '600' },
  sourceChipTextActive: { color: BRAND.blue, fontWeight: '800' },
  dealLinkChip: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 10, backgroundColor: BRAND.white, borderWidth: 1, borderColor: BRAND.line, marginRight: 8, maxWidth: 130 },
  dealLinkChipActive: { backgroundColor: BRAND.mint + '20', borderColor: BRAND.mint },
  dealLinkText: { fontSize: 11, color: BRAND.sub, fontWeight: '600' },
  dealLinkTextActive: { color: BRAND.mint, fontWeight: '800' },
  durationRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 14 },
  durationChip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 10, backgroundColor: BRAND.white, borderWidth: 1, borderColor: BRAND.line },
  durationChipActive: { backgroundColor: BRAND.mint + '20', borderColor: BRAND.mint },
  durationText: { fontSize: 12, fontWeight: '700', color: BRAND.sub },
  durationTextActive: { color: BRAND.mint },
  datePickerBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: BRAND.white, borderRadius: 12, borderWidth: 1, borderColor: BRAND.line, padding: 12, marginBottom: 12 },
  datePickerText: { fontSize: 14, color: BRAND.text },
  datePickerWrapper: { backgroundColor: BRAND.white, borderRadius: 12, marginBottom: 12 },
  datePickerDone: { padding: 12 },
  tagInputRow: { flexDirection: 'row', gap: 8, marginBottom: 8 },
  addTagBtn: { width: 44, height: 44, borderRadius: 12, backgroundColor: BRAND.blue + '15', alignItems: 'center', justifyContent: 'center' },

  // Deal Detail
  detailSection: { marginBottom: 18 },
  detailSectionTitle: { fontSize: 13, fontWeight: '800', color: BRAND.sub, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 },
  detailCard: { backgroundColor: BRAND.white, borderRadius: 14, padding: 14, borderWidth: 1, borderColor: BRAND.line },
  detailRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  detailRowLabel: { width: 80, fontSize: 12, color: BRAND.sub, fontWeight: '600' },
  detailRowValue: { flex: 1, fontSize: 13, color: BRAND.text, fontWeight: '600', textAlign: 'right' },
  detailActionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  detailActionBtn: { alignItems: 'center', gap: 5, width: (width - 80) / 5 },
  detailActionIcon: { width: 44, height: 44, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  detailActionLabel: { fontSize: 10, color: BRAND.text, fontWeight: '600', textAlign: 'center' },
  dealNoteText: { fontSize: 13, color: BRAND.text, lineHeight: 20 },
  scoreBadgeLg: { width: 36, height: 36, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  scoreBadgeLgText: { color: '#fff', fontSize: 13, fontWeight: '900' },
  noteInputRow: { flexDirection: 'row', gap: 8, marginBottom: 14 },
  noteInput: { flex: 1, backgroundColor: BRAND.white, borderRadius: 12, borderWidth: 1, borderColor: BRAND.line, paddingHorizontal: 12, paddingVertical: 10, fontSize: 13, color: BRAND.text },
  noteAddBtn: { width: 40, height: 40, borderRadius: 12, backgroundColor: BRAND.blue, alignItems: 'center', justifyContent: 'center' },
  noteItem: { flexDirection: 'row', gap: 10, marginBottom: 14 },
  noteTimeline: { alignItems: 'center', paddingTop: 4 },
  noteDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: BRAND.blue },
  noteLineV: { flex: 1, width: 1, backgroundColor: BRAND.line, marginTop: 4 },
  noteText: { fontSize: 13, color: BRAND.text, lineHeight: 20, marginBottom: 4 },
  noteMeta: { fontSize: 11, color: BRAND.sub },

  // Contact Detail
  contactDetailHeader: { flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 16 },
  contactAvatarLg: { width: 56, height: 56, borderRadius: 28, alignItems: 'center', justifyContent: 'center' },
  contactAvatarLgText: { fontSize: 20, fontWeight: '900', color: BRAND.blue },
  contactDetailName: { fontSize: 18, fontWeight: '900', color: BRAND.text, marginBottom: 3 },
  contactDetailRole: { fontSize: 13, color: BRAND.sub, marginBottom: 2 },
  contactDetailCompany: { fontSize: 13, color: BRAND.blue, fontWeight: '700' },
  contactDealName: { fontSize: 13, fontWeight: '800', color: BRAND.text, marginBottom: 3 },
  contactDealStage: { fontSize: 11, fontWeight: '700' },

  // AI Advisor
  aiWelcome: { alignItems: 'center', paddingVertical: 30, paddingHorizontal: 20 },
  aiAvatarBig: { width: 70, height: 70, borderRadius: 20, backgroundColor: '#7c3aed20', alignItems: 'center', justifyContent: 'center', marginBottom: 16 },
  aiWelcomeTitle: { fontSize: 20, fontWeight: '900', color: BRAND.text, marginBottom: 10 },
  aiWelcomeSub: { fontSize: 13, color: BRAND.sub, textAlign: 'center', lineHeight: 20 },
  aiPromptsTitle: { fontSize: 13, fontWeight: '800', color: BRAND.sub, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 },
  aiPromptsGrid: { gap: 8 },
  aiPromptBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: BRAND.white, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 12, borderWidth: 1, borderColor: BRAND.line },
  aiPromptText: { flex: 1, fontSize: 13, color: BRAND.text, fontWeight: '600' },
  chatBubbleRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, marginBottom: 14 },
  chatAvatar: { width: 28, height: 28, borderRadius: 8, backgroundColor: '#7c3aed20', alignItems: 'center', justifyContent: 'center' },
  chatBubble: { maxWidth: width * 0.75, borderRadius: 16, padding: 12 },
  chatBubbleAI: { backgroundColor: BRAND.white, borderWidth: 1, borderColor: BRAND.line, borderBottomLeftRadius: 4 },
  chatBubbleUser: { backgroundColor: BRAND.blue, borderBottomRightRadius: 4 },
  chatBubbleText: { fontSize: 13, color: BRAND.text, lineHeight: 20 },
  chatTime: { fontSize: 10, color: BRAND.sub, marginTop: 4 },
  chatInputRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 10, padding: 14, backgroundColor: BRAND.white, borderTopWidth: 1, borderTopColor: BRAND.line },
  chatInput: { flex: 1, backgroundColor: BRAND.bg, borderRadius: 12, borderWidth: 1, borderColor: BRAND.line, paddingHorizontal: 14, paddingVertical: 10, fontSize: 14, color: BRAND.text, maxHeight: 100 },
  chatSendBtn: { width: 42, height: 42, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },

  // Email Templates
  templateCard: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: BRAND.white, borderRadius: 14, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: BRAND.line },
  templateIcon: { width: 42, height: 42, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  templateName: { fontSize: 14, fontWeight: '800', color: BRAND.text, marginBottom: 4 },
  templatePreview: { fontSize: 12, color: BRAND.sub, lineHeight: 17 },
  emailSubject: { fontSize: 14, fontWeight: '700', color: BRAND.text, marginTop: 4 },
  emailBody: { fontSize: 13, color: BRAND.text, lineHeight: 22, marginTop: 6 },
  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 16 },
  backBtnText: { fontSize: 13, color: BRAND.blue, fontWeight: '700' },

  // Team
  teamCard: { backgroundColor: BRAND.white, borderRadius: 16, padding: 16, marginBottom: 14, borderWidth: 1, borderColor: BRAND.line },
  teamCardHeader: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14 },
  teamRankBadge: { width: 26, height: 26, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  teamRankText: { color: '#fff', fontSize: 11, fontWeight: '900' },
  teamCardName: { fontSize: 15, fontWeight: '800', color: BRAND.text },
  teamCardRole: { fontSize: 11, color: BRAND.sub, marginTop: 2 },
  teamCardWinRate: { fontSize: 11, color: BRAND.sub, fontWeight: '600' },
  teamStatsRow: { flexDirection: 'row', gap: 8 },
  teamStat: { flex: 1, alignItems: 'center', backgroundColor: BRAND.bg, borderRadius: 10, padding: 8 },
  teamStatValue: { fontSize: 16, fontWeight: '900', color: BRAND.text },
  teamStatLabel: { fontSize: 9, color: BRAND.sub, fontWeight: '600', marginTop: 2, textAlign: 'center' },

  // Kanban
  kanbanCol: { width: 200, marginRight: 12, flex: 1 },
  kanbanColHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, paddingVertical: 8, borderRadius: 10, marginBottom: 4 },
  kanbanColTitle: { flex: 1, color: '#fff', fontSize: 12, fontWeight: '800' },
  kanbanCount: { backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: 5, paddingHorizontal: 5, paddingVertical: 1 },
  kanbanCountText: { color: '#fff', fontSize: 10, fontWeight: '900' },
  kanbanValue: { fontSize: 12, fontWeight: '700', marginBottom: 8, paddingHorizontal: 2 },
  kanbanCard: { backgroundColor: BRAND.white, borderRadius: 12, padding: 12, marginBottom: 10, borderWidth: 1, borderColor: BRAND.line },
  kanbanCardCompany: { fontSize: 13, fontWeight: '800', color: BRAND.text, marginBottom: 2 },
  kanbanCardContact: { fontSize: 11, color: BRAND.sub, marginBottom: 8 },
  kanbanCardValue: { fontSize: 14, fontWeight: '900', marginBottom: 6 },
  kanbanCardFooter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 },
  kanbanProbText: { fontSize: 10, color: BRAND.sub, fontWeight: '600' },
  kanbanEmpty: { backgroundColor: BRAND.line + '40', borderRadius: 10, padding: 20, alignItems: 'center' },
  kanbanEmptyText: { fontSize: 12, color: BRAND.sub },

  // Filters
  sortOptionRow: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 12, marginBottom: 8, backgroundColor: BRAND.white, borderWidth: 1, borderColor: BRAND.line },
  sortOptionText: { flex: 1, fontSize: 14, color: BRAND.text, fontWeight: '600' },

  // Submit btn
  submitBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 14, paddingVertical: 14 },
  submitBtnText: { color: '#fff', fontWeight: '800', fontSize: 15 },
});